import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  PlusIcon,
  MinusIcon,
} from '@heroicons/react/24/outline'

import {
  ArrowTrendingUpIcon,
  ArrowRightCircleIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
} from '@heroicons/react/24/solid'

import {
  useGetCoachOrgTrailSessionPadsQuery,
} from '../coachOrgTrailSessionPads/coachOrgTrailSessionPadsApiSlice'

import {
  useGetOrgTrailSessionCohortsQuery,
} from '../orgTrailSessionCohorts/orgTrailSessionCohortsApiSlice'

import {
  useGetUserTrailSessionPlansQuery,
} from '../userTrailSessions/userTrailSessionPlansApiSlice'

// 04 Jul 2026 - Coach Trail Board Session Q&A tab
import EtUserSessionQNABoard from '../userSessionQNAResponses/EtUserSessionQNABoard'

// 04 Jul 2026 - Coach Trail Board cohort scoped evaluations.
import {
  useGetUserChallengeCoachEvalsQuery,
} from '../userChallengeCoachEvals/userChallengeCoachEvalsApiSlice'

import PulseLoader from 'react-spinners/PulseLoader'
import { getTimeSince } from '@/utils/EtGetTimeSince'

const EtCoachOrgTrailBoardCompProd = ({
  coachId,
  orgId,
  trailId,
  orgName,
  trailName,
  trailPic,
}) => {

    // First Created - 03 Jul 2026
    // Author - Atul Pandey
    // Purpose - Trail level coaching workspace for one coach, organization, and trail.
    // Updated - 03 Jul 2026 - Initial shell with trail summary and session navigation.
    // Updated - 03 Jul 2026 - Added Trail Board section tabs and Journey timeline.
    // Updated - 04 Jul 2026 - Refinements for the "Cohort" section
    // Updated - 21 Jul 2026 - Refinements for note text sizes

    // 0 - Initialize navigation
    const navigate = useNavigate()

    // 1 - Manage the active Coach Trail Board section.
    // Journey is the default section.
    const [activeTrailBoardTab, setActiveTrailBoardTab] =
        useState('journey')

    // 1a - Manage the selected session filter in Coach's Notes.
    // "all" shows notes from every session in the selected trail.
    const [selectedCoachNoteSessionId, setSelectedCoachNoteSessionId] =
        useState('all')

    // 1b - Manage the selected Coach Note category filter.
    // "all" shows Self, All Users, Self for User, and Shared with User notes.
    const [selectedCoachNoteCategory, setSelectedCoachNoteCategory] =
        useState('all')

    // 1c - Manage expanded Coach Note details in the Trail Board.
    const [showTrailCoachNoteDetails, setShowTrailCoachNoteDetails] =
        useState({})

    // 21 Jul 2026 - Default display size for expanded Coach Trail Board notes
    const [trailCoachNoteTextSize, setTrailCoachNoteTextSize] =
        useState('medium')

    // 21 Jul 2026 - Individual note text size overrides
    const [
        trailCoachNoteTextSizeByKey,
        setTrailCoachNoteTextSizeByKey,
    ] = useState({})

    const trailCoachNoteTextSizeOptions = [
        {
            value: 'small',
            label: 'S',
            title: 'Small text',
            className: 'text-[11px] leading-5',
        },
        {
            value: 'medium',
            label: 'M',
            title: 'Medium text',
            className: 'text-sm leading-6',
        },
        {
            value: 'large',
            label: 'L',
            title: 'Large text',
            className: 'text-base leading-7',
        },
        {
            value: 'extraLarge',
            label: 'XL',
            title: 'Extra large text',
            className: 'text-lg leading-8',
        },
    ]

    const trailCoachNoteTextSizeClasses = {
        small: 'text-[11px] leading-5',
        medium: 'text-sm leading-6',
        large: 'text-base leading-7',
        extraLarge: 'text-lg leading-8',
    }

    // 1d - Manage the selected organization trail cohort for this Coach Trail Board.
    //
    // The selected cohort scopes notes, Journey engagement counts,
    // Session Q&A data, Evaluations, and Actions at the trail level.
    const [selectedCohortId, setSelectedCohortId] =
        useState('')

    // 1e - Manage the selected user filter for user-specific Coach Notes.
    //
    // This filter applies only when the selected category is:
    // Self for User
    // Shared with User
    const [selectedCoachNoteUserId, setSelectedCoachNoteUserId] =
        useState('all')

    // 04 Jul 2026

    // 1f - Manage expanded Cohort member rows.
    //
    // Each key represents one selected cohort user ID.
    const [showCohortMemberDetails, setShowCohortMemberDetails] =
        useState({})

    // 1g - Manage the Cohort member coaching coverage filter.
    //
    // This filters the Cohort Members table only.
    // It does not change the selected cohort, Journey, or Coach's Notes data.
    const [selectedCohortCoverageFilter, setSelectedCohortCoverageFilter] =
        useState('all')

    // 1h - Manage the selected Trail Session for the Session Q&A tab.
    //
    // "all" is not used here because the existing Session Q&A Board is designed
    // to render one session's Q&A sets at a time.
    const [selectedTrailQNASessionId, setSelectedTrailQNASessionId] =
        useState('')

    // 1i - Manage the selected evaluation Session filter.
    //
    // "all" shows Coach evaluations from every Session in the selected trail.
    const [selectedEvaluationSessionId, setSelectedEvaluationSessionId] =
        useState('all')

    // 1j - Manage the selected evaluated user filter.
    //
    // "all" shows evaluations for every user in the selected cohort.
    const [selectedEvaluationUserId, setSelectedEvaluationUserId] =
        useState('all')

    // 1k - Manage expanded evaluation user rows.
    const [showEvaluationUserDetails, setShowEvaluationUserDetails] =
        useState({})


    // 2 - Get all Coach Org Trail Session Pads for the selected Coach,
    // Organization, and Trail.
    //
    // sessionId is intentionally omitted because this board consolidates
    // the complete trail rather than one individual Coach Session Pad.
    const {
        data: coachOrgTrailSessionPadsData,
        isLoading,
        isError,
        error,
    } = useGetCoachOrgTrailSessionPadsQuery(
        {
        coachId,
        orgId,
        trailId,
        active: true,
        },
        {
        skip: !coachId || !orgId || !trailId,
        }
    )

    // 2a - Get all active cohorts for the selected Organization and Trail.
    //
    // The Coach Trail Board operates at Organization + Trail level,
    // therefore cohort selection is also resolved at this level.
    const {
        data: orgTrailSessionCohortsData,
        isLoading: isCohortsLoading,
        isError: isCohortsError,
        error: cohortsError,
    } = useGetOrgTrailSessionCohortsQuery(
        {
            orgId,
            trailId,
            active: true,
        },
        {
            skip: !orgId || !trailId,
        }
    )

    // 2b - Convert normalized cohort entities into an ordered active cohort list.
    const orgTrailSessionCohorts =
    orgTrailSessionCohortsData?.ids
        ?.map(
        (id) =>
            orgTrailSessionCohortsData?.entities[id]
        )
        ?.filter(
        (cohort) =>
            cohort?.orgId?._id === orgId &&
            cohort?.trailId?._id === trailId &&
            cohort?.active !== false
        )
        ?.sort(
        (firstCohort, secondCohort) =>
            (firstCohort?.cohortSequence || 0) -
            (secondCohort?.cohortSequence || 0)
    ) || []

    // 2c - Default the board to the first active cohort.
    //
    // This mirrors the existing Coach Session Pad behavior.
    React.useEffect(() => {
        if (
            !selectedCohortId &&
            orgTrailSessionCohorts.length > 0
        ) {
            setSelectedCohortId(
            orgTrailSessionCohorts[0]._id
            )
        }
        }, [
        selectedCohortId,
        orgTrailSessionCohorts,
    ])

    // 2d - Resolve the selected cohort record for display and later board sections.
    const selectedCohort =
    orgTrailSessionCohorts.find(
        (cohort) =>
        cohort?._id?.toString() ===
        selectedCohortId?.toString()
    ) || null

    // 2e - Get active user trail session plans for the selected trail.
    //
    // These plans provide the complete user list for the selected cohort,
    // across all sessions in the trail.
    const {
    data: userTrailSessionPlansData,
    isLoading: isUserTrailSessionPlansLoading,
    isError: isUserTrailSessionPlansError,
    error: userTrailSessionPlansError,
    } = useGetUserTrailSessionPlansQuery(
    {
        trailId,
        active: true,
    },
    {
        skip: !trailId,
    }
    )

    // 2f - Convert normalized User Trail Session Plans into an array.
    const userTrailSessionPlans =
        userTrailSessionPlansData?.ids
            ?.map(
            (id) =>
                userTrailSessionPlansData?.entities[id]
            )
            ?.filter(
            (plan) =>
                plan?.trailId?._id?.toString() ===
                trailId?.toString() &&
                plan?.active !== false
    ) || []

    // 2g - Resolve all distinct users belonging to the selected Trail Board cohort.
    //
    // A user may have one active plan per session. Deduplicate by user ID so
    // each person appears only once in the user filter chips.
    const selectedCohortUsers = Array.from(
        new Map(
            userTrailSessionPlans
            .filter((plan) => {
                const planCohortId =
                plan?.cohortId?._id ||
                plan?.cohortId

                return (
                planCohortId?.toString() ===
                selectedCohortId?.toString()
                )
            })
            .filter((plan) => plan?.userId?._id)
            .map((plan) => [
                plan.userId._id.toString(),
                plan.userId,
            ])
        ).values()
        ).sort((firstUser, secondUser) => {
            return (firstUser?.name || '').localeCompare(
                secondUser?.name || ''
            )
    })

    // 2h - Return the first name for compact Trail Board user chips.
    const getFirstName = (fullName = '') => {
    return fullName
        .trim()
        .split(/\s+/)
        .filter(Boolean)[0] || 'User'
    }

    // 2i - Reset the selected user when user-specific filtering is not active,
    // when the selected cohort changes, or when the selected user no longer
    // belongs to the selected cohort.
    useEffect(() => {
        const isUserSpecificCategory =
            selectedCoachNoteCategory === 'Self for User' ||
            selectedCoachNoteCategory === 'Shared with User'

        const selectedUserExistsInCohort =
            selectedCohortUsers.some(
            (user) =>
                user?._id?.toString() ===
                selectedCoachNoteUserId?.toString()
            )

        if (!isUserSpecificCategory) {
            if (selectedCoachNoteUserId !== 'all') {
            setSelectedCoachNoteUserId('all')
            }

            return
        }

        if (
            selectedCoachNoteUserId !== 'all' &&
            !selectedUserExistsInCohort
        ) {
            setSelectedCoachNoteUserId('all')
        }
    }, [
        selectedCohortId,
        selectedCoachNoteCategory,
        selectedCoachNoteUserId,
        selectedCohortUsers,
    ])

    // 2j - Get all Coach challenge evaluation records.
    //
    // The Board filters these records locally by Coach, organization, trail,
    // selected cohort, and selected cohort users.
    const {
        data: userChallengeCoachEvalsData,
        isLoading: isCoachEvaluationsLoading,
        isError: isCoachEvaluationsError,
        error: coachEvaluationsError,
    } = useGetUserChallengeCoachEvalsQuery()

    // 3 - Convert RTK normalized data into an ordered list of the Coach's
    // Session Pads for the selected Organization and Trail.
    const coachTrailSessionPads =
        coachOrgTrailSessionPadsData?.ids
        ?.map((id) => coachOrgTrailSessionPadsData?.entities[id])
        ?.filter(
            (pad) =>
            pad?.coachId?._id === coachId &&
            pad?.orgId?._id === orgId &&
            pad?.trailId?._id === trailId &&
            pad?.active !== false
        )
        ?.sort(
            (firstPad, secondPad) =>
            (firstPad?.sessionId?.sessionSequence || 0) -
            (secondPad?.sessionId?.sessionSequence || 0)
    ) || []

    // 3a - Resolve a populated cohort reference or raw cohort ID.
    const getCohortId = (value) => {
        if (!value) {
            return null
        }

        return value?._id || value
    }

    // 3b - Check whether one Coach note belongs to the selected Board cohort.
    //
    // Coach Org Trail Session Pads are shared across cohorts.
    // The cohort is stored at the individual note level.
    const isSelectedCohortNote = (note) => {
        const noteCohortId = getCohortId(note?.cohortId)

        return (
            noteCohortId?.toString() ===
            selectedCohortId?.toString()
        )
    }

    // 3c - Return only notes belonging to the selected Board cohort.
    //
    // When no cohort is selected yet, return no notes rather than mixing
    // data from different cohorts.
    const getSelectedCohortNotes = (notes = []) => {
        if (!selectedCohortId) {
            return []
        }

        return notes.filter(isSelectedCohortNote)
    }

    // 4 - Build the ordered Journey session array for the selected cohort.
    //
    // Session Pads remain available across the entire trail.
    // Only the cohort-scoped note arrays are used for Journey engagement counts.
    const journeySessions = coachTrailSessionPads.map((pad) => ({
    padId: pad?._id,

    sessionId: pad?.sessionId?._id,

    sessionName:
        pad?.sessionId?.sessionName || 'Session',

    sessionSequence:
        pad?.sessionId?.sessionSequence || 0,

    sessionPic:
        pad?.sessionId?.pic || '',

    // 4a - Coach private notes for the selected cohort.
    coachSessionSelfNotes: getSelectedCohortNotes(
        pad?.coachSessionSelfNotes || []
    ),

    // 4b - Coach notes shared with all users in the selected cohort.
    coachSessionAllUsersNotes: getSelectedCohortNotes(
        pad?.coachSessionAllUsersNotes || []
    ),

    // 4c - Coach private notes about specific users in the selected cohort.
    coachSessionSelfUserNotes: getSelectedCohortNotes(
        pad?.coachSessionSelfUserNotes || []
    ),

    // 4d - Coach notes shared with specific users in the selected cohort.
    coachSessionSharedUserNotes: getSelectedCohortNotes(
        pad?.coachSessionSharedUserNotes || []
    ),

    // 4e - Session Q&A setup remains session-level.
    // Response and contribution calculations will be cohort-scoped later.
    sessionQNASets:
        pad?.sessionId?.sessionQNASets?.filter(
        (sessionQNASet) =>
            sessionQNASet?.active !== false
        ) || [],
    }))


    // 04 Jul 2026 - Session Q&A
    // 4a1 - Resolve all Trail Sessions that have at least one active Q&A set.
    //
    // The selected cohort is passed into EtUserSessionQNABoard.
    // Session Q&A setup itself is defined at the Session level.
    const trailQNASessions = journeySessions
    .filter(
        (session) =>
        session?.sessionQNASets?.length > 0
    )
    .sort(
        (firstSession, secondSession) =>
        (firstSession?.sessionSequence || 0) -
        (secondSession?.sessionSequence || 0)
    )

    // 4a2 - Default the Q&A tab to the first Q&A-enabled Session.
    //
    // This resets automatically when the available Q&A session list changes.
    useEffect(() => {
    const selectedSessionStillAvailable =
        trailQNASessions.some(
        (session) =>
            session?.sessionId?.toString() ===
            selectedTrailQNASessionId?.toString()
        )

    if (
        !selectedSessionStillAvailable &&
        trailQNASessions.length > 0
    ) {
        setSelectedTrailQNASessionId(
        trailQNASessions[0].sessionId
        )
    }
    }, [
    selectedTrailQNASessionId,
    trailQNASessions,
    ])

    // 4a3 - Resolve the selected Q&A Session for the Trail Board.
    const selectedTrailQNASession =
    trailQNASessions.find(
        (session) =>
        session?.sessionId?.toString() ===
        selectedTrailQNASessionId?.toString()
    ) || null

    // 4a4 - Resolve the Coach's organization ID for the existing Coach Q&A Board.
    //
    // The Coach Session Pad uses coachId.userOrgId for this same prop.
    const coachOrgId =
    coachTrailSessionPads[0]?.coachId?.userOrgId?._id ||
    coachTrailSessionPads[0]?.coachId?.userOrgId ||
    ''

    // 4a5 - Toggle full note detail in the consolidated Coach's Notes section.
    const handleToggleTrailCoachNoteDetails = (noteKey) => {
    setShowTrailCoachNoteDetails((previous) => ({
        ...previous,
        [noteKey]: !previous[noteKey],
    }))
    }

    // 4b - Convert a populated MongoDB reference or raw ID into one comparable ID.
    const getEntityId = (value) => {
    if (!value) {
        return null
    }

    return value?._id || value
    }

    // 4c - Convert stored HTML note content into a compact plain-text preview.
    const getPlainNotePreview = (noteText) => {
    if (!noteText) {
        return ''
    }

    return noteText
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/p>/gi, ' ')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    }

    // 4d - Format a Coach note date for Trail Board display.
    const formatTrailBoardDate = (dateValue) => {
    if (!dateValue) {
        return '—'
    }

    const parsedDate = new Date(dateValue)

    if (Number.isNaN(parsedDate.getTime())) {
        return '—'
    }

    return parsedDate.toLocaleDateString(
        'en-US',
        {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        }
    )
    }

    // 4e - Build one normalized Coach Note record.
    //
    // The Coach Session Pad stores four different note array types.
    // This helper gives all four types one common board-level shape.
    const buildNormalizedCoachNotes = ({
    notes = [],
    pad,
    noteCategory,
    noteTextKey,
    noteDateKey,
    noteUpdateDateKey,
    isUserSpecific = false,
    }) => {
    return notes
        .filter((note) => note?.delete !== true)
        .map((note) => {

           
            // 4e1 - Resolve the user from the current Session Pad.
            //
            // User-specific note records may carry only a raw user ID.
            // sessionUsers is the best available source for the populated user name
            // and email belonging to that Session Pad.
            const noteUser = note?.userId

            const userId = getEntityId(noteUser)

            const matchingSessionUser = pad?.sessionUsers?.find((sessionUser) => {
            const sessionUserId = getEntityId(sessionUser?.userId)

                    return (
                        sessionUserId?.toString() ===
                        userId?.toString()
                    )
            })

            const resolvedUser =
            matchingSessionUser?.userId ||
            noteUser ||
            null

            return {
            noteId: note?._id,
            noteCategory,
            noteText: note?.[noteTextKey] || '',
            noteDate: note?.[noteDateKey] || null,
            noteUpdateDate: note?.[noteUpdateDateKey] || null,

            sessionId: getEntityId(pad?.sessionId),
            sessionSequence:
                pad?.sessionId?.sessionSequence || 0,
            sessionName:
                pad?.sessionId?.sessionName || 'Session',
            sessionPic:
                pad?.sessionId?.pic || '',

            userId: isUserSpecific ? userId : null,
            userName: isUserSpecific
                ? resolvedUser?.name || ''
                : '',
            userEmail: isUserSpecific
                ? resolvedUser?.email || ''
                : '',
            }
        })
        .filter((note) => note?.noteText?.trim())
    }

    // 4f - Consolidate Coach Notes across all sessions for the selected cohort.
    //
    // Every Coach note is filtered by note.cohortId before normalization.
    // This prevents notes from one cohort appearing in another cohort's Trail Board.
    const allTrailCoachNotes = coachTrailSessionPads
    .flatMap((pad) => {
        const selfNotes = buildNormalizedCoachNotes({
        notes: getSelectedCohortNotes(
            pad?.coachSessionSelfNotes || []
        ),
        pad,
        noteCategory: 'Self',
        noteTextKey: 'coachSelfNoteText',
        noteDateKey: 'coachSelfNoteDate',
        noteUpdateDateKey: 'coachSelfNoteUpdateDate',
        })

        const allUsersNotes = buildNormalizedCoachNotes({
        notes: getSelectedCohortNotes(
            pad?.coachSessionAllUsersNotes || []
        ),
        pad,
        noteCategory: 'All Users',
        noteTextKey: 'coachAllUsersNoteText',
        noteDateKey: 'coachAllUsersNoteDate',
        noteUpdateDateKey: 'coachAllUsersNoteUpdateDate',
        })

        const selfForUserNotes = buildNormalizedCoachNotes({
        notes: getSelectedCohortNotes(
            pad?.coachSessionSelfUserNotes || []
        ),
        pad,
        noteCategory: 'Self for User',
        noteTextKey: 'coachSelfUserNoteText',
        noteDateKey: 'coachSelfUserNoteDate',
        noteUpdateDateKey: 'coachSelfUserNoteUpdateDate',
        isUserSpecific: true,
        })

        const sharedWithUserNotes = buildNormalizedCoachNotes({
        notes: getSelectedCohortNotes(
            pad?.coachSessionSharedUserNotes || []
        ),
        pad,
        noteCategory: 'Shared with User',
        noteTextKey: 'coachSharedUserNoteText',
        noteDateKey: 'coachSharedUserNoteDate',
        noteUpdateDateKey: 'coachSharedUserNoteUpdateDate',
        isUserSpecific: true,
        })

        return [
        ...selfNotes,
        ...allUsersNotes,
        ...selfForUserNotes,
        ...sharedWithUserNotes,
        ]
    })
    .sort((firstNote, secondNote) => {
        const firstDate =
        firstNote?.noteUpdateDate ||
        firstNote?.noteDate ||
        0

        const secondDate =
        secondNote?.noteUpdateDate ||
        secondNote?.noteDate ||
        0

        return new Date(secondDate) - new Date(firstDate)
    })

    // 4g - Build one Session filter chip only for sessions that contain Coach Notes.
    const coachNoteSessionFilters = journeySessions
    .map((session) => {
        const sessionNoteCount = allTrailCoachNotes.filter(
        (note) =>
            note?.sessionId?.toString() ===
            session?.sessionId?.toString()
        ).length

        return {
        sessionId: session?.sessionId,
        sessionSequence: session?.sessionSequence,
        sessionName: session?.sessionName,
        sessionNoteCount,
        }
    })
    .filter((session) => session?.sessionNoteCount > 0)

    // 4h - First filter Coach Notes by the selected Session.
    //
    // This source list is deliberately filtered only by Session.
    // Category counts must respond to the selected Session, even when the
    // selected Category chip is currently different.
    const sessionFilteredTrailCoachNotes = allTrailCoachNotes.filter((note) => {
    return (
        selectedCoachNoteSessionId === 'all' ||
        note?.sessionId?.toString() ===
        selectedCoachNoteSessionId?.toString()
    )
    })

    // 4i - Resolve Coach Note category counts from the selected Session scope.
    //
    // Example:
    // When Session 1 is selected and it contains three Self notes,
    // the Self chip shows 3 even when other trail sessions contain more Self notes.
    const coachSelfNotesCount = sessionFilteredTrailCoachNotes.filter(
    (note) => note?.noteCategory === 'Self'
    ).length

    const coachAllUsersNotesCount = sessionFilteredTrailCoachNotes.filter(
    (note) => note?.noteCategory === 'All Users'
    ).length

    const coachSelfForUserNotesCount = sessionFilteredTrailCoachNotes.filter(
    (note) => note?.noteCategory === 'Self for User'
    ).length

    const coachSharedWithUserNotesCount =
    sessionFilteredTrailCoachNotes.filter(
        (note) => note?.noteCategory === 'Shared with User'
    ).length

    // 4j - Apply selected Category and selected User after Session filtering.
    //
    // User filtering applies only to Self for User and Shared with User notes.
    const filteredTrailCoachNotes =
    sessionFilteredTrailCoachNotes.filter((note) => {
        const matchesCategory =
        selectedCoachNoteCategory === 'all' ||
        note?.noteCategory === selectedCoachNoteCategory

        const isUserSpecificCategory =
        selectedCoachNoteCategory === 'Self for User' ||
        selectedCoachNoteCategory === 'Shared with User'

        const matchesUser =
        !isUserSpecificCategory ||
        selectedCoachNoteUserId === 'all' ||
        note?.userId?.toString() ===
            selectedCoachNoteUserId?.toString()

        return matchesCategory && matchesUser
    })

    // 04 Jul 2026
    // 4k - Resolve the active selected cohort's user session plans.
    //
    // One user normally has one active plan per session in the selected cohort.
    // These plans are the source of truth for the Cohort tab's planned-session
    // coverage calculations.
    const selectedCohortUserPlans = userTrailSessionPlans.filter((plan) => {
    const planCohortId =
        plan?.cohortId?._id ||
        plan?.cohortId

    return (
        plan?.active !== false &&
        planCohortId?.toString() ===
        selectedCohortId?.toString()
    )
    })

    // 4k1 - Convert normalized Coach evaluation data into an array.
    const userChallengeCoachEvals =
    userChallengeCoachEvalsData?.ids
        ?.map(
        (id) =>
            userChallengeCoachEvalsData?.entities[id]
        )
        ?.filter(Boolean) || []

    // 4k2 - Resolve selected cohort user IDs once.
    //
    // Evaluations must belong to one of these users, even if an old or
    // malformed evaluation record carries the selected cohortId.
    const selectedCohortUserIdSet = new Set(
    selectedCohortUsers
        .map((user) => user?._id?.toString())
        .filter(Boolean)
    )

    // 4k3 - Resolve all Coach evaluation records for this Board's:
    //
    // Coach
    // User organization
    // Trail
    // Selected cohort
    // Selected cohort users
    //
    // cohortId is the fast reporting reference. selectedCohortUsers provides an
    // additional safety boundary from the authoritative cohort membership view.
    const selectedCohortCoachEvals =
    userChallengeCoachEvals.filter((evaluationRecord) => {
        const evaluationCoachId =
        getEntityId(evaluationRecord?.coachId)

        const evaluationUserOrgId =
        getEntityId(evaluationRecord?.userOrgId)

        const evaluationTrailId =
        getEntityId(evaluationRecord?.trailId)

        const evaluationCohortId =
        getEntityId(evaluationRecord?.cohortId)

        const evaluationUserId =
        getEntityId(evaluationRecord?.userId)

        return (
        evaluationRecord?.active !== false &&
        evaluationCoachId?.toString() ===
            coachId?.toString() &&
        evaluationUserOrgId?.toString() ===
            orgId?.toString() &&
        evaluationTrailId?.toString() ===
            trailId?.toString() &&
        evaluationCohortId?.toString() ===
            selectedCohortId?.toString() &&
        selectedCohortUserIdSet.has(
            evaluationUserId?.toString()
        )
        )
    })

    // 4k4 - Build Session filters only for Sessions that contain Coach evaluations.
    const evaluationSessionFilters = journeySessions
    .map((session) => {
        const sessionEvaluationCount =
        selectedCohortCoachEvals.filter(
            (evaluationRecord) =>
            getEntityId(
                evaluationRecord?.sessionId
            )?.toString() ===
            session?.sessionId?.toString()
        ).length

        return {
        sessionId: session?.sessionId,
        sessionSequence: session?.sessionSequence || 0,
        sessionName: session?.sessionName || 'Session',
        sessionEvaluationCount,
        }
    })
    .filter(
        (session) =>
        session?.sessionEvaluationCount > 0
    )

    // 4k5 - Apply selected Session and selected User filters.
    //
    // This becomes the source list for the Evaluations tab table and summary.
    const filteredSelectedCohortCoachEvals =
    selectedCohortCoachEvals.filter((evaluationRecord) => {
        const evaluationSessionId =
        getEntityId(evaluationRecord?.sessionId)

        const evaluationUserId =
        getEntityId(evaluationRecord?.userId)

        const matchesSession =
        selectedEvaluationSessionId === 'all' ||
        evaluationSessionId?.toString() ===
            selectedEvaluationSessionId?.toString()

        const matchesUser =
        selectedEvaluationUserId === 'all' ||
        evaluationUserId?.toString() ===
            selectedEvaluationUserId?.toString()

        return matchesSession && matchesUser
    })

    // 4k6 - Reset Evaluation filters when their selected values no longer
    // exist in the active selected cohort.
    //
    // This prevents a stale Session or User filter from hiding every record
    // after the Coach changes the Board-level cohort.
    useEffect(() => {
    const selectedSessionStillAvailable =
        selectedEvaluationSessionId === 'all' ||
        evaluationSessionFilters.some(
        (session) =>
            session?.sessionId?.toString() ===
            selectedEvaluationSessionId?.toString()
        )

    if (!selectedSessionStillAvailable) {
        setSelectedEvaluationSessionId('all')
    }

    const selectedUserStillAvailable =
        selectedEvaluationUserId === 'all' ||
        selectedCohortUsers.some(
        (user) =>
            user?._id?.toString() ===
            selectedEvaluationUserId?.toString()
        )

    if (!selectedUserStillAvailable) {
        setSelectedEvaluationUserId('all')
    }
    }, [
    selectedCohortId,
    selectedEvaluationSessionId,
    selectedEvaluationUserId,
    evaluationSessionFilters,
    selectedCohortUsers,
    ])

    // 4k7 - Resolve one comparable numeric Coach score.
    //
    // Empty strings and nonnumeric values must not affect score totals
    // or averages.
    const getValidCoachScore = (scoreValue) => {
    const parsedScore = Number(scoreValue)

    if (
        scoreValue === '' ||
        scoreValue === null ||
        scoreValue === undefined ||
        Number.isNaN(parsedScore)
    ) {
        return null
    }

    return parsedScore
    }

    // 4k8 - Resolve a readable challenge name from the selected evaluation record.
    //
    // The Coach evaluation GET endpoint populates session challenge definitions.
    // Fall back gracefully when a legacy record has incomplete population.
    const getEvaluationChallengeName = (evaluationRecord) => {
    const evaluationChallengeId =
        getEntityId(evaluationRecord?.challengeId)

    const matchingSessionChallenge =
        evaluationRecord?.sessionId?.sessionChallenges?.find(
        (sessionChallenge) =>
            getEntityId(
            sessionChallenge?.challengeId
            )?.toString() ===
            evaluationChallengeId?.toString()
        )

    return (
        matchingSessionChallenge?.challengeId?.challengeName ||
        evaluationRecord?.challengeId?.challengeName ||
        'Challenge'
    )
    }

    // 4k9 - Resolve a readable Session identity for an evaluation record.
    const getEvaluationSessionDetails = (evaluationRecord) => {
    const evaluationSessionId =
        getEntityId(evaluationRecord?.sessionId)

    const matchingJourneySession =
        journeySessions.find(
        (session) =>
            session?.sessionId?.toString() ===
            evaluationSessionId?.toString()
        )

    return {
        sessionId: evaluationSessionId,
        sessionSequence:
        matchingJourneySession?.sessionSequence ||
        evaluationRecord?.sessionId?.sessionSequence ||
        0,
        sessionName:
        matchingJourneySession?.sessionName ||
        evaluationRecord?.sessionId?.sessionName ||
        'Session',
        sessionPic:
        matchingJourneySession?.sessionPic ||
        evaluationRecord?.sessionId?.pic ||
        '',
    }
    }

    // 4l0 - Build one evaluation coverage record for every user in the
    // selected cohort.
    //
    // This intentionally starts from selectedCohortUsers, not from evaluation
    // records, so users without any Coach evaluation are visible as
    // "Needs Evaluation."
    const cohortEvaluationCoverage = selectedCohortUsers
    .map((user) => {
        const cohortUserId =
        user?._id?.toString()

        if (!cohortUserId) {
        return null
        }

        const userEvaluationRecords =
        selectedCohortCoachEvals.filter(
            (evaluationRecord) =>
            getEntityId(
                evaluationRecord?.userId
            )?.toString() === cohortUserId
        )

        const userEvaluationDetails =
        userEvaluationRecords.map((evaluationRecord) => {
            const sessionDetails =
            getEvaluationSessionDetails(evaluationRecord)

            const dimensionEvaluations =
            evaluationRecord?.userChallengeCoachEvals || []

            const scoredDimensionEvaluations =
            dimensionEvaluations
                .map((dimensionEvaluation) => ({
                ...dimensionEvaluation,
                validScore: getValidCoachScore(
                    dimensionEvaluation?.userChallengeDimCoachScore
                ),
                }))
                .filter(
                (dimensionEvaluation) =>
                    dimensionEvaluation?.validScore !== null
                )

            const scoreTotal =
            scoredDimensionEvaluations.reduce(
                (total, dimensionEvaluation) =>
                total + dimensionEvaluation.validScore,
                0
            )

            const averageScore =
            scoredDimensionEvaluations.length > 0
                ? scoreTotal /
                scoredDimensionEvaluations.length
                : null

            return {
            evaluationId: evaluationRecord?._id,

            challengeId: getEntityId(
                evaluationRecord?.challengeId
            ),

            challengeName:
                getEvaluationChallengeName(evaluationRecord),

            sessionId: sessionDetails.sessionId,
            sessionSequence:
                sessionDetails.sessionSequence,
            sessionName:
                sessionDetails.sessionName,
            sessionPic:
                sessionDetails.sessionPic,

            dimensionEvaluations,
            scoredDimensionCount:
                scoredDimensionEvaluations.length,
            averageScore,

            createdAt:
                evaluationRecord?.createdAt || null,

            updatedAt:
                evaluationRecord?.updatedAt ||
                evaluationRecord?.createdAt ||
                null,
            }
        })

        const evaluatedSessionIdSet = new Set(
        userEvaluationDetails
            .map((evaluationDetail) =>
            evaluationDetail?.sessionId?.toString()
            )
            .filter(Boolean)
        )

        const evaluatedChallengeIdSet = new Set(
        userEvaluationDetails
            .map((evaluationDetail) =>
            evaluationDetail?.challengeId?.toString()
            )
            .filter(Boolean)
        )

        const allScoredDimensions =
        userEvaluationDetails.flatMap(
            (evaluationDetail) =>
            evaluationDetail?.dimensionEvaluations || []
        )
            .map((dimensionEvaluation) => ({
            ...dimensionEvaluation,
            validScore: getValidCoachScore(
                dimensionEvaluation?.userChallengeDimCoachScore
            ),
            }))
            .filter(
            (dimensionEvaluation) =>
                dimensionEvaluation?.validScore !== null
            )

        const totalScore =
        allScoredDimensions.reduce(
            (total, dimensionEvaluation) =>
            total + dimensionEvaluation.validScore,
            0
        )

        const averageScore =
        allScoredDimensions.length > 0
            ? totalScore / allScoredDimensions.length
            : null

        const latestEvaluationDate =
        userEvaluationDetails.reduce(
            (latestDate, evaluationDetail) => {
            const activityDate =
                evaluationDetail?.updatedAt ||
                evaluationDetail?.createdAt

            if (!activityDate) {
                return latestDate
            }

            if (!latestDate) {
                return activityDate
            }

            return new Date(activityDate) >
                new Date(latestDate)
                ? activityDate
                : latestDate
            },
            null
        )

        let evaluationStatus = 'Needs Evaluation'

        if (userEvaluationDetails.length > 0) {
        evaluationStatus =
            allScoredDimensions.length > 0
            ? 'Evaluated'
            : 'Evaluation Started'
        }

        return {
        userId: cohortUserId,
        userName: user?.name || 'User',
        userEmail: user?.email || '',
        username: user?.username || '',

        evaluationRecordCount:
            userEvaluationDetails.length,

        evaluatedSessionCount:
            evaluatedSessionIdSet.size,

        evaluatedChallengeCount:
            evaluatedChallengeIdSet.size,

        scoredDimensionCount:
            allScoredDimensions.length,

        averageScore,

        latestEvaluationDate,
        evaluationStatus,

        evaluationDetails:
            userEvaluationDetails.sort(
            (firstEvaluation, secondEvaluation) =>
                (firstEvaluation?.sessionSequence || 0) -
                (secondEvaluation?.sessionSequence || 0)
            ),
        }
    })
    .filter(Boolean)
    .sort((firstUser, secondUser) =>
        (firstUser?.userName || '').localeCompare(
        secondUser?.userName || ''
        )
    )

    // 4l1 - Apply the Evaluation tab's selected Session and User filters.
    //
    // cohortEvaluationCoverage remains the full selected-cohort source.
    // This derived list controls only the visible evaluation coverage table.
    const filteredCohortEvaluationCoverage =
    cohortEvaluationCoverage.filter((userCoverage) => {
        const matchesUser =
        selectedEvaluationUserId === 'all' ||
        userCoverage?.userId?.toString() ===
            selectedEvaluationUserId?.toString()

        const matchesSession =
        selectedEvaluationSessionId === 'all' ||
        userCoverage?.evaluationDetails?.some(
            (evaluationDetail) =>
            evaluationDetail?.sessionId?.toString() ===
            selectedEvaluationSessionId?.toString()
        )

        return matchesUser && matchesSession
    })

    // 4l2 - Trail Board Evaluation summary counts for the selected cohort.
    const cohortEvaluatedUserCount =
    cohortEvaluationCoverage.filter(
        (userCoverage) =>
        userCoverage?.evaluationStatus === 'Evaluated'
    ).length

    const cohortEvaluationStartedUserCount =
    cohortEvaluationCoverage.filter(
        (userCoverage) =>
        userCoverage?.evaluationStatus ===
        'Evaluation Started'
    ).length

    const cohortUsersNeedingEvaluationCount =
    cohortEvaluationCoverage.filter(
        (userCoverage) =>
        userCoverage?.evaluationStatus ===
        'Needs Evaluation'
    ).length

    const cohortEvaluationRecordCount =
    selectedCohortCoachEvals.length

    const cohortScoredDimensionCount =
    cohortEvaluationCoverage.reduce(
        (total, userCoverage) =>
        total +
        (userCoverage?.scoredDimensionCount || 0),
        0
    )

    const cohortEvaluationScoreTotal =
    cohortEvaluationCoverage.reduce(
        (total, userCoverage) => {
        const userScoreCount =
            userCoverage?.scoredDimensionCount || 0

        const userAverageScore =
            userCoverage?.averageScore

        if (
            !userScoreCount ||
            userAverageScore === null ||
            userAverageScore === undefined
        ) {
            return total
        }

        return (
            total +
            userAverageScore * userScoreCount
        )
        },
        0
    )

    const cohortAverageEvaluationScore =
    cohortScoredDimensionCount > 0
        ? cohortEvaluationScoreTotal /
        cohortScoredDimensionCount
        : null

    // 4l3 - Toggle expanded evaluation detail for one cohort user.
    const handleToggleEvaluationUserDetails = (
    evaluationUserId
    ) => {
    setShowEvaluationUserDetails((previous) => ({
        ...previous,
        [evaluationUserId]: !previous[evaluationUserId],
    }))
    }




    // 4l - Build a trail-level engagement record for every user in the selected cohort.
    //
    // This is deliberately based only on user-specific Coach Notes:
    //
    // Self for User
    // Shared with User
    //
    // Generic Self and All Users notes are cohort-level notes and must not be
    // treated as individual coaching engagement for a particular user.
    const cohortMemberEngagements = selectedCohortUsers
    .map((user) => {
        const userId =
        user?._id?.toString()

        if (!userId) {
        return null
        }

        // 4l1 - Get this user's active planned sessions in the selected cohort.
        const userPlans = selectedCohortUserPlans
        .filter((plan) => {
            const planUserId =
            plan?.userId?._id ||
            plan?.userId

            return (
            planUserId?.toString() ===
            userId
            )
        })

        // 4l2 - Deduplicate plans by session because one session should appear
        // only once in the Cohort user's session-level breakdown.
        const userSessionPlansBySessionId = Array.from(
        new Map(
            userPlans
            .map((plan) => {
                const plannedSessionId =
                plan?.sessionId?._id ||
                plan?.sessionId

                if (!plannedSessionId) {
                return null
                }

                return [
                plannedSessionId.toString(),
                plan,
                ]
            })
            .filter(Boolean)
        ).values()
        )

        // 4l3 - Get all user-specific Coach Notes for this user across the trail.
        const userSpecificCoachNotes =
        allTrailCoachNotes.filter((note) => {
            const noteUserId =
            note?.userId?.toString()

            const isUserSpecificNote =
            note?.noteCategory === 'Self for User' ||
            note?.noteCategory === 'Shared with User'

            return (
            isUserSpecificNote &&
            noteUserId === userId
            )
        })

        // 4l4 - Separate private Coach observations from notes shared with the user.
        const selfForUserNotes =
        userSpecificCoachNotes.filter(
            (note) =>
            note?.noteCategory === 'Self for User'
        )

        const sharedWithUserNotes =
        userSpecificCoachNotes.filter(
            (note) =>
            note?.noteCategory === 'Shared with User'
        )

        // 4l5 - Resolve the latest individual Coach activity for this user.
        const latestCoachActivityDate =
        userSpecificCoachNotes.reduce(
            (latestDate, note) => {
            const noteActivityDate =
                note?.noteUpdateDate ||
                note?.noteDate

            if (!noteActivityDate) {
                return latestDate
            }

            if (!latestDate) {
                return noteActivityDate
            }

            return new Date(noteActivityDate) > new Date(latestDate)
                ? noteActivityDate
                : latestDate
            },
            null
        )

        // 4l6 - Build a per-session engagement breakdown for this user.
        //
        // This will be rendered when the user row is expanded in the next step.
        const sessionBreakdown =
        userSessionPlansBySessionId
            .map((plan) => {
            const plannedSessionId =
                plan?.sessionId?._id ||
                plan?.sessionId

            const matchingJourneySession =
                journeySessions.find(
                (session) =>
                    session?.sessionId?.toString() ===
                    plannedSessionId?.toString()
                )

            const sessionSelfForUserNotes =
                selfForUserNotes.filter(
                (note) =>
                    note?.sessionId?.toString() ===
                    plannedSessionId?.toString()
                )

            const sessionSharedWithUserNotes =
                sharedWithUserNotes.filter(
                (note) =>
                    note?.sessionId?.toString() ===
                    plannedSessionId?.toString()
                )

            const sessionLastCoachActivityDate = [
                ...sessionSelfForUserNotes,
                ...sessionSharedWithUserNotes,
            ].reduce((latestDate, note) => {
                const noteActivityDate =
                note?.noteUpdateDate ||
                note?.noteDate

                if (!noteActivityDate) {
                return latestDate
                }

                if (!latestDate) {
                return noteActivityDate
                }

                return new Date(noteActivityDate) > new Date(latestDate)
                ? noteActivityDate
                : latestDate
            }, null)

            return {
                sessionId: plannedSessionId,
                sessionSequence:
                matchingJourneySession?.sessionSequence ||
                plan?.sessionId?.sessionSequence ||
                0,
                sessionName:
                matchingJourneySession?.sessionName ||
                plan?.sessionId?.sessionName ||
                'Session',
                sessionPic:
                matchingJourneySession?.sessionPic ||
                plan?.sessionId?.pic ||
                '',
                selfForUserNoteCount:
                sessionSelfForUserNotes.length,
                sharedWithUserNoteCount:
                sessionSharedWithUserNotes.length,
                lastCoachActivityDate:
                sessionLastCoachActivityDate,
            }
            })
            .sort(
            (firstSession, secondSession) =>
                (firstSession?.sessionSequence || 0) -
                (secondSession?.sessionSequence || 0)
            )

        // 4l7 - Resolve the Coach attention status for this user.
        //
        // Engaged          → at least one private Coach note
        // Shared Guidance  → no private note, but at least one shared note
        // Needs Attention  → no individual Coach notes of either type
        let engagementStatus = 'Needs Attention'

        if (selfForUserNotes.length > 0) {
        engagementStatus = 'Engaged'
        } else if (sharedWithUserNotes.length > 0) {
        engagementStatus = 'Shared Guidance'
        }

        return {
        userId,
        userName: user?.name || 'User',
        userEmail: user?.email || '',
        username: user?.username || '',

        plannedSessionCount:
            userSessionPlansBySessionId.length,

        selfForUserNoteCount:
            selfForUserNotes.length,

        sharedWithUserNoteCount:
            sharedWithUserNotes.length,

        latestCoachActivityDate,
        engagementStatus,
        sessionBreakdown,
        }
    })
    .filter(Boolean)
    .sort((firstUser, secondUser) =>
        (firstUser?.userName || '').localeCompare(
        secondUser?.userName || ''
        )
    )

    // 4m - Resolve Cohort tab summary counts.
    const cohortUserCount =
    cohortMemberEngagements.length

    const cohortSessionCount =
    Array.from(
        new Set(
        selectedCohortUserPlans
            .map((plan) => {
            const planSessionId =
                plan?.sessionId?._id ||
                plan?.sessionId

            return planSessionId?.toString()
            })
            .filter(Boolean)
        )
    ).length

    const cohortUsersWithSelfNotesCount =
    cohortMemberEngagements.filter(
        (member) =>
        member?.selfForUserNoteCount > 0
    ).length

    const cohortUsersWithSharedNotesCount =
    cohortMemberEngagements.filter(
        (member) =>
        member?.sharedWithUserNoteCount > 0
    ).length

    const cohortUsersNeedingAttentionCount =
    cohortMemberEngagements.filter(
        (member) =>
        member?.engagementStatus === 'Needs Attention'
    ).length

    // 4n - Open the originating Coach Session Pad for the selected note.
    //
    // We do not edit inside the Trail Board. Existing Session Pad edit flows
    // remain the source of truth for note updates.
    const handleOpenCoachNoteSession = (note) => {
        if (!note?.sessionId) {
            return
        }

        navigate(
            `/etapp/coach/update/${coachId}/${orgId}/${trailId}/${note.sessionId}`
        )
    }

    // 4o - 04 Jul 2026 - Toggle one Cohort member's session-level engagement detail.
    const handleToggleCohortMemberDetails = (userId) => {
    setShowCohortMemberDetails((previous) => ({
        ...previous,
        [userId]: !previous[userId],
    }))
    }

    // 4p - 04 Jul 2026 - Filter Cohort Members by individual coaching coverage status.
    //
    // The base cohortMemberEngagements array always contains every user in the
    // selected cohort. This derived list only controls the visible table rows.
    const filteredCohortMemberEngagements =
    cohortMemberEngagements.filter((member) => {
        if (selectedCohortCoverageFilter === 'all') {
        return true
        }

        return (
        member?.engagementStatus ===
        selectedCohortCoverageFilter
        )
    })


    // 5 - Get the total coach-created note count for one session.
    //
    // This count is currently across all cohorts. Cohort-level filtering
    // will be added when the board-level cohort selector is introduced.
    const getCoachSessionNoteCount = (session) => {
        const selfNoteCount =
        session?.coachSessionSelfNotes?.filter(
            (note) => note?.delete !== true
        )?.length || 0

        const allUsersNoteCount =
        session?.coachSessionAllUsersNotes?.filter(
            (note) => note?.delete !== true
        )?.length || 0

        const selfUserNoteCount =
        session?.coachSessionSelfUserNotes?.filter(
            (note) => note?.delete !== true
        )?.length || 0

        const sharedUserNoteCount =
        session?.coachSessionSharedUserNotes?.filter(
            (note) => note?.delete !== true
        )?.length || 0

        return (
        selfNoteCount +
        allUsersNoteCount +
        selfUserNoteCount +
        sharedUserNoteCount
        )
    }

    // 6 - Resolve the Coach engagement state for one Journey session.
    //
    // Unlike the User Trail Board, the Coach board does not use the user's
    // Planned, Started, In Progress, or Completed execution status.
    const getCoachSessionEngagementStatus = (session) => {
        const noteCount = getCoachSessionNoteCount(session)
        const qnaSetCount = session?.sessionQNASets?.length || 0

        if (noteCount > 0) {
        return 'Engaged'
        }

        if (qnaSetCount > 0) {
        return 'Q&A Ready'
        }

        return 'Not Engaged'
    }

    // 7 - Resolve Journey node styling from the Coach engagement state.
    const getJourneyNodeClasses = (session) => {
        const engagementStatus =
        getCoachSessionEngagementStatus(session)

        if (engagementStatus === 'Engaged') {
        return 'border-orange-600 bg-orange-600 text-white'
        }

        if (engagementStatus === 'Q&A Ready') {
        return 'border-lime-500 bg-lime-100 text-lime-700'
        }

        return 'border-gray-300 bg-white text-gray-400'
    }

    // 8 - Resolve connector line styling between Journey nodes.
    const getJourneyLineClasses = (session) => {
        const engagementStatus =
        getCoachSessionEngagementStatus(session)

        if (
        engagementStatus === 'Engaged' ||
        engagementStatus === 'Q&A Ready'
        ) {
        return 'bg-lime-500'
        }

        return 'bg-gray-200'
    }

    // 9 - Open an individual Coach Org Trail Session Pad from the Journey.
    const handleJourneySessionClick = (session) => {
        const sessionId = session?.sessionId

        if (!sessionId) {
        return
        }

        navigate(
        `/etapp/coach/update/${coachId}/${orgId}/${trailId}/${sessionId}`
        )
    }

    // 10 - Loading display for Trail Pads, Cohorts, and Trail User Plans.
    if (
        isLoading ||
        isCohortsLoading ||
        isUserTrailSessionPlansLoading ||
        isCoachEvaluationsLoading
    ) {
        return (
        <div className="m-1 rounded bg-white p-3">
            <PulseLoader color="#ea580c" size={7} />
        </div>
        )
    }

    // 11 - Error display for Trail Pads, Cohorts, and Trail User Plans.
    if (
        isError ||
        isCohortsError ||
        isUserTrailSessionPlansError ||
        isCoachEvaluationsError
    ) {
        return (
        <div className="m-1 rounded bg-white p-3">
            <p className="text-xs text-red-500">
            {error?.data?.message ||
                cohortsError?.data?.message ||
                userTrailSessionPlansError?.data?.message ||
                coachEvaluationsError?.data?.message ||
                'Unable to load the Coach Trail Board.'}
            </p>
        </div>
        )
    }

    return (
        <div className="m-1 space-y-2">
        {/* 12 - Coach Trail Board Header */}
        <div className="rounded bg-white px-3 py-2 shadow-sm">
            <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center">
                <div className="mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100">
                {trailPic ? (
                    <img
                    src={trailPic}
                    alt=""
                    className="h-6 w-6"
                    />
                ) : (
                    <ArrowTrendingUpIcon className="h-5 w-5 text-orange-600" />
                )}
                </div>

                <div className="min-w-0">
                <p className="truncate text-sm font-bold text-gray-800">
                    {trailName}
                </p>

                <p className="truncate text-[10px] text-gray-500">
                    Coach Trail Board · {orgName}
                </p>
                </div>
            </div>

            <div className="hidden items-center sm:flex">
                <CalendarDaysIcon className="mr-1 h-4 w-4 text-orange-600" />
                <p className="text-[10px] font-semibold text-orange-600">
                {journeySessions.length} Sessions
                </p>
            </div>
            </div>

            {/* 12a - Board-level engagement cohort selector */}
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-2">
            <div className="flex min-w-0 items-center">
                <UserGroupIcon className="mr-1 h-4 w-4 shrink-0 text-orange-600" />

                <p className="mr-2 text-[10px] font-bold text-gray-500">
                Cohort
                </p>

                <p className="truncate text-[10px] font-semibold text-gray-700">
                {selectedCohort?.cohortName || 'No Cohort Selected'}
                </p>
            </div>

            <div className="flex flex-wrap justify-end gap-1">
                {orgTrailSessionCohorts.map((cohort, cohortIndex) => {
                const isSelected =
                    cohort?._id?.toString() ===
                    selectedCohortId?.toString()

                return (
                    <button
                    key={cohort._id}
                    type="button"
                    onClick={() =>
                        setSelectedCohortId(cohort._id)
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                        isSelected
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    >
                    {cohortIndex + 1}. {cohort?.cohortName}
                    </button>
                )
                })}
            </div>
            </div>


        </div>

        {/* 13 - Coach Trail Board section tabs */}
        <div className="rounded bg-white px-3 p-2 shadow-sm">
            <div className="flex overflow-x-auto">
            <button
                type="button"
                onClick={() => setActiveTrailBoardTab('journey')}
                className={`shrink-0 border-b-2 px-3 py-2 text-[10px] font-bold ${
                activeTrailBoardTab === 'journey'
                    ? 'border-orange-600 text-orange-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
            >
                Journey
            </button>

            <button
                type="button"
                onClick={() => setActiveTrailBoardTab('coachNotes')}
                className={`shrink-0 border-b-2 px-3 py-2 text-[10px] font-bold ${
                activeTrailBoardTab === 'coachNotes'
                    ? 'border-orange-600 text-orange-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
            >
                Coach&apos;s Notes
            </button>

            <button
                type="button"
                onClick={() => setActiveTrailBoardTab('cohort')}
                className={`shrink-0 border-b-2 px-3 py-2 text-[10px] font-bold ${
                activeTrailBoardTab === 'cohort'
                    ? 'border-orange-600 text-orange-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
            >
                Cohort
            </button>

            <button
                type="button"
                onClick={() => setActiveTrailBoardTab('sessionQuestions')}
                className={`shrink-0 border-b-2 px-3 py-2 text-[10px] font-bold ${
                activeTrailBoardTab === 'sessionQuestions'
                    ? 'border-orange-600 text-orange-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
            >
                Session Q&amp;As
            </button>

            <button
                type="button"
                onClick={() => setActiveTrailBoardTab('evaluations')}
                className={`shrink-0 border-b-2 px-3 py-2 text-[10px] font-bold ${
                activeTrailBoardTab === 'evaluations'
                    ? 'border-orange-600 text-orange-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
            >
                Evaluations
            </button>

            {/* Future Coach Trail Board section */}
            {/* <button
                type="button"
                onClick={() => setActiveTrailBoardTab('actions')}
                className={`shrink-0 border-b-2 px-3 py-2 text-[10px] font-bold ${
                activeTrailBoardTab === 'actions'
                    ? 'border-orange-600 text-orange-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
            >
                Actions
            </button> */}
            </div>
        </div>

        {/* 14 - Journey timeline section */}
        {activeTrailBoardTab === 'journey' && (
            <div className="rounded bg-white p-3 shadow-sm">
            <div className="mb-5">
                <p className="text-sm font-bold text-gray-800">
                Coaching Journey
                </p>

                <p className="text-[10px] text-gray-500">
                Coaching engagement across the sessions in this trail.
                </p>
            </div>

            {journeySessions.length > 0 ? (
                <div className="overflow-x-auto pb-3">
                <div className="flex min-w-max items-start px-2">
                    {/* 14a - Render one Journey node for every Trail Session */}
                    {journeySessions.map((session, sessionIndex) => {
                    const engagementStatus =
                        getCoachSessionEngagementStatus(session)

                    const noteCount =
                        getCoachSessionNoteCount(session)

                    const qnaSetCount =
                        session?.sessionQNASets?.length || 0

                    return (
                        <React.Fragment
                        key={
                            session?.padId ||
                            session?.sessionId ||
                            sessionIndex
                        }
                        >
                        <button
                            type="button"
                            onClick={() =>
                            handleJourneySessionClick(session)
                            }
                            className="group flex w-28 shrink-0 flex-col items-center rounded p-1 text-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-400"
                            title={`Open ${
                            session?.sessionName || 'session'
                            }`}
                        >
                            {/* 14b - Clickable session sequence node */}
                            <div
                            className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold transition-transform group-hover:scale-110 ${getJourneyNodeClasses(
                                session
                            )}`}
                            >
                            {session?.sessionSequence ||
                                sessionIndex + 1}
                            </div>

                            {/* 14c - Session name */}
                            <p className="mt-2 min-h-[30px] px-1 text-[10px] font-bold text-gray-700 group-hover:text-orange-700">
                            {session?.sessionName || 'Session'}
                            </p>

                            {/* 14d - Coach engagement status */}
                            <p
                            className={`mt-1 rounded-full px-2 py-0.5 text-[9px] font-medium ${
                                engagementStatus === 'Engaged'
                                ? 'bg-orange-100 text-orange-700'
                                : engagementStatus === 'Q&A Ready'
                                ? 'bg-lime-100 text-lime-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                            >
                            {engagementStatus}
                            </p>

                            {/* 14e - Coach engagement summary */}
                            <p className="mt-1 min-h-[15px] text-[9px] text-gray-500">
                            {noteCount > 0
                                ? `${noteCount} Notes`
                                : qnaSetCount > 0
                                ? `${qnaSetCount} Q&A Sets`
                                : 'No notes yet'}
                            </p>

                            <ArrowRightCircleIcon className="mt-1 h-4 w-4 text-lime-600 opacity-0 transition-opacity group-hover:opacity-100" />
                        </button>

                        {/* 14f - Connector line between Journey nodes */}
                        {sessionIndex < journeySessions.length - 1 && (
                            <div className="flex w-12 shrink-0 items-start pt-3">
                            <div
                                className={`h-1 w-full rounded ${getJourneyLineClasses(
                                session
                                )}`}
                            />
                            </div>
                        )}
                        </React.Fragment>
                    )
                    })}
                </div>
                </div>
            ) : (
                <p className="text-xs text-gray-500">
                No Coach Session Pads are available for this trail.
                </p>
            )}
            </div>
        )}

        {/* 15 - Consolidated Coach's Notes across all Trail Sessions */}
        {activeTrailBoardTab === 'coachNotes' && (
        <div className="rounded bg-white p-3 shadow-sm">
            {/* 15a - Coach's Notes title and visible result count */}
            <div className="mb-3 flex items-start justify-between gap-2">
            <div>
                <p className="text-sm font-bold text-gray-800">
                Coach&apos;s Notes
                </p>

                <p className="text-[10px] text-gray-500">
                Consolidated coaching notes across all sessions in this trail.
                </p>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
                {/* 21 Jul 2026 - Default text size for expanded notes */}
                <div className="flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-1">
                    <p className="mr-1 text-[9px] font-semibold text-gray-500">
                        Note text:
                    </p>

                    {trailCoachNoteTextSizeOptions.map((sizeOption) => {
                        const isSelected =
                            trailCoachNoteTextSize === sizeOption.value

                        return (
                            <button
                                key={sizeOption.value}
                                type="button"
                                title={sizeOption.title}
                                aria-label={sizeOption.title}
                                aria-pressed={isSelected}
                                onClick={() => {
                                    setTrailCoachNoteTextSize(sizeOption.value)

                                    // Reset note-level overrides when the global size changes
                                    setTrailCoachNoteTextSizeByKey({})
                                }}
                                className={`min-w-7 rounded px-2 py-1 text-[9px] font-semibold transition ${
                                    isSelected
                                        ? 'bg-orange-600 text-white shadow-sm'
                                        : 'border border-gray-200 bg-white text-gray-500 hover:border-orange-300 hover:text-orange-600'
                                }`}
                            >
                                {sizeOption.label}
                            </button>
                        )
                    })}
                </div>

                <div className="flex h-5 min-w-5 items-center justify-center rounded-full border border-orange-300 bg-orange-50 px-1">
                    <p className="text-[10px] font-bold text-orange-600">
                        {filteredTrailCoachNotes.length}
                    </p>
                </div>
            </div>

            {/* <div className="flex h-5 min-w-5 items-center justify-center rounded-full border border-orange-300 bg-orange-50 px-1">
                <p className="text-[10px] font-bold text-orange-600">
                {filteredTrailCoachNotes.length}
                </p>
            </div> */}
            </div>

            {/* 15b - Session, Category, and conditional User filters */}
            {allTrailCoachNotes.length > 0 && (
            <div className="mb-3 space-y-2">
                {/* 15b1 - Session chips on left and category chips on right */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                {/* 15b1a - Session filter chips */}
                <div className="flex flex-wrap gap-2">
                    <button
                    type="button"
                    onClick={() => setSelectedCoachNoteSessionId('all')}
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                        selectedCoachNoteSessionId === 'all'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    >
                    All ({allTrailCoachNotes.length})
                    </button>

                    {coachNoteSessionFilters.map((session) => {
                    const isSelected =
                        selectedCoachNoteSessionId?.toString() ===
                        session?.sessionId?.toString()

                    return (
                        <button
                        key={session.sessionId}
                        type="button"
                        onClick={() =>
                            setSelectedCoachNoteSessionId(
                            session.sessionId
                            )
                        }
                        className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                            isSelected
                            ? 'bg-orange-600 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                        >
                        {session?.sessionSequence
                            ? `Session ${session.sessionSequence}`
                            : session?.sessionName}{' '}
                        ({session?.sessionNoteCount})
                        </button>
                    )
                    })}
                </div>

                {/* 15b1b - Category filter chips, right justified */}
                <div className="flex flex-wrap justify-end gap-2">
                    <button
                    type="button"
                    onClick={() => setSelectedCoachNoteCategory('all')}
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                        selectedCoachNoteCategory === 'all'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    >
                    All ({sessionFilteredTrailCoachNotes.length})
                    </button>

                    <button
                    type="button"
                    onClick={() => setSelectedCoachNoteCategory('Self')}
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                        selectedCoachNoteCategory === 'Self'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    >
                    Self ({coachSelfNotesCount})
                    </button>

                    <button
                    type="button"
                    onClick={() =>
                        setSelectedCoachNoteCategory('All Users')
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                        selectedCoachNoteCategory === 'All Users'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    >
                    All Users ({coachAllUsersNotesCount})
                    </button>

                    <button
                    type="button"
                    onClick={() =>
                        setSelectedCoachNoteCategory('Self for User')
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                        selectedCoachNoteCategory === 'Self for User'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    >
                    Self for User ({coachSelfForUserNotesCount})
                    </button>

                    <button
                    type="button"
                    onClick={() =>
                        setSelectedCoachNoteCategory('Shared with User')
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                        selectedCoachNoteCategory === 'Shared with User'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    >
                    Shared with User ({coachSharedWithUserNotesCount})
                    </button>
                </div>
                </div>

                {/* 15b2 - User chips only for user-specific Coach Note categories */}
                {(selectedCoachNoteCategory === 'Self for User' ||
                selectedCoachNoteCategory === 'Shared with User') && (
                <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-2">
                    <button
                    type="button"
                    onClick={() => setSelectedCoachNoteUserId('all')}
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                        selectedCoachNoteUserId === 'all'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    >
                    All Users
                    </button>

                    {selectedCohortUsers.map((user) => {
                    const isSelected =
                        selectedCoachNoteUserId?.toString() ===
                        user?._id?.toString()

                    return (
                        <button
                        key={user._id}
                        type="button"
                        onClick={() =>
                            setSelectedCoachNoteUserId(user._id)
                        }
                        className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                            isSelected
                            ? 'bg-orange-600 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                        title={user?.name || ''}
                        >
                        {getFirstName(user?.name)}
                        </button>
                    )
                    })}
                </div>
                )}
            </div>
            )}            

            {/* 15c - Consolidated Coach Note table */}
            {filteredTrailCoachNotes.length > 0 ? (
            <div className="overflow-x-auto">
                <div className="min-w-[1100px]">
                {/* 15c1 - Coach Note table header */}
                <div className="grid grid-cols-12 rounded bg-gray-100 px-2 py-2">
                    <div className="col-span-3">
                    <p className="text-[10px] font-bold text-gray-600">
                        Session
                    </p>
                    </div>

                    <div className="col-span-2">
                    <p className="text-[10px] font-bold text-gray-600">
                        Category
                    </p>
                    </div>

                    <div className="col-span-2">
                    <p className="text-[10px] font-bold text-gray-600">
                        User
                    </p>
                    </div>

                    <div className="col-span-3">
                    <p className="text-[10px] font-bold text-gray-600">
                        Note
                    </p>
                    </div>

                    <div className="col-span-1">
                    <p className="text-[10px] font-bold text-gray-600">
                        Created
                    </p>
                    </div>

                    <div className="col-span-1">
                    <p className="text-[10px] font-bold text-gray-600">
                        Updated
                    </p>
                    </div>
                </div>

                {/* 15c2 - Consolidated Coach Note rows */}
                {filteredTrailCoachNotes.map((note, noteIndex) => {
                    const expandedNoteKey =
                    `${note?.noteId}-${note?.sessionId}-${note?.noteCategory}`

                    // 21 Jul 2026 - Resolve this note's local size or use the global default
                    const selectedNoteTextSize =
                        trailCoachNoteTextSizeByKey[expandedNoteKey] ||
                        trailCoachNoteTextSize

                    const selectedNoteTextSizeClass =
                        trailCoachNoteTextSizeClasses[selectedNoteTextSize] ||
                        trailCoachNoteTextSizeClasses.medium

                    const firstCreatedDate = note?.noteDate

                    const lastUpdatedDate =
                    note?.noteUpdateDate ||
                    note?.noteDate

                    const isUserSpecific =
                    note?.noteCategory === 'Self for User' ||
                    note?.noteCategory === 'Shared with User'

                    // console.log('note', note)

                    return (
                    <div
                        key={expandedNoteKey}
                        className="border-b border-gray-100 px-2 py-2 last:border-b-0 hover:bg-orange-50"
                    >
                        <div className="grid grid-cols-12 items-center">
                        {/* 15c2a - Number, expand control, session picture, and session name */}
                        <div className="col-span-3 flex items-center pr-2">
                            <p className="mr-1 text-md font-bold text-gray-800">
                            {noteIndex + 1}.
                            </p>

                            <button
                            type="button"
                            onClick={() =>
                                handleToggleTrailCoachNoteDetails(
                                expandedNoteKey
                                )
                            }
                            className="mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-white"
                            title={
                                showTrailCoachNoteDetails[expandedNoteKey]
                                ? 'Hide Coach note detail'
                                : 'Show Coach note detail'
                            }
                            >
                            {showTrailCoachNoteDetails[expandedNoteKey] ? (
                                <MinusIcon className="h-4 w-4 text-orange-600" />
                            ) : (
                                <PlusIcon className="h-4 w-4 text-orange-600" />
                            )}
                            </button>

                            <div className="mr-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-50">
                            {note?.sessionPic ? (
                                <img
                                src={note.sessionPic}
                                alt=""
                                className="h-5 w-5"
                                />
                            ) : (
                                <p className="text-[10px] font-bold text-gray-400">
                                {note?.sessionSequence || '?'}
                                </p>
                            )}
                            </div>

                            <div className="min-w-0">
                            <p className="text-[10px] font-bold text-gray-700">
                                {note?.sessionSequence
                                ? `Session ${note.sessionSequence}`
                                : 'Session'}
                            </p>

                            <p className="truncate text-[10px] text-orange-600">
                                {note?.sessionName}
                            </p>
                            </div>
                        </div>

                        {/* 15c2b - Note category label */}
                        <div className="col-span-2">
                            <p
                            className={`w-fit rounded-full px-2 py-1 text-[9px] font-semibold ${
                                note?.noteCategory === 'Self'
                                ? 'bg-gray-200 text-gray-700'
                                : note?.noteCategory === 'All Users'
                                ? 'bg-yellow-100 text-yellow-800'
                                : note?.noteCategory === 'Self for User'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-lime-100 text-lime-700'
                            }`}
                            >
                            {note?.noteCategory}
                            </p>
                        </div>

                        {/* 15c2c - User visibility based on Coach Note category */}
                        <div className="col-span-2 pr-2">
                        {isUserSpecific ? (
                            <div>
                            <p className="truncate text-[10px] font-semibold text-gray-700">
                                {note?.userName || 'Unknown User'}
                            </p>

                            {note?.userEmail && (
                                <p className="truncate text-[9px] text-gray-400">
                                {note.userEmail}
                                </p>
                            )}
                            </div>
                        ) : note?.noteCategory === 'All Users' ? (
                            <p className="text-[10px] font-semibold text-gray-700">
                            All
                            </p>
                        ) : (
                            <p className="text-[10px] text-gray-400">
                            —
                            </p>
                        )}
                        </div>

                        {/* 15c2d - Compact Coach Note preview */}
                        <div className="col-span-3 pr-3">
                            <p className="line-clamp-2 text-[11px] text-gray-700">
                            {getPlainNotePreview(note?.noteText)}
                            </p>
                        </div>

                        {/* 15c2e - First created date */}
                        <div className="col-span-1 flex items-center">
                            <ClockIcon className="mr-1 h-4 w-4 shrink-0 text-lime-600" />

                            <p className="text-[9px] text-gray-600">
                            {formatTrailBoardDate(firstCreatedDate)}
                            </p>
                        </div>

                        {/* 15c2f - Relative update time */}
                        <div className="col-span-1">
                            {lastUpdatedDate ? (
                            <>
                                <p className="w-fit rounded-full bg-lime-200 px-2 py-[1px] text-[9px] text-gray-700">
                                {getTimeSince(lastUpdatedDate)}
                                </p>

                                <p className="mt-0.5 text-[7px] italic text-gray-400">
                                last updated
                                </p>
                            </>
                            ) : (
                            <p className="text-[10px] text-gray-400">
                                —
                            </p>
                            )}
                        </div>
                        </div>

                        {/* 15c2g - Expanded full Coach Note detail and source session action */}
                        {showTrailCoachNoteDetails[expandedNoteKey] && (
                        <div className="m-1 mt-2 rounded bg-gray-50 p-2">

                            {/* 21 Jul 2026 - Individual Coach note text size selector */}
                            <div className="mb-2 flex justify-end">
                                <div className="flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1">
                                    <p className="mr-1 text-[9px] font-semibold text-gray-500">
                                        Text:
                                    </p>

                                    {trailCoachNoteTextSizeOptions.map((sizeOption) => {
                                        const isSelected =
                                            selectedNoteTextSize === sizeOption.value

                                        return (
                                            <button
                                                key={sizeOption.value}
                                                type="button"
                                                title={sizeOption.title}
                                                aria-label={sizeOption.title}
                                                aria-pressed={isSelected}
                                                onClick={() =>
                                                    setTrailCoachNoteTextSizeByKey(
                                                        (previous) => ({
                                                            ...previous,
                                                            [expandedNoteKey]:
                                                                sizeOption.value,
                                                        })
                                                    )
                                                }
                                                className={`min-w-7 rounded px-2 py-1 text-[9px] font-semibold transition ${
                                                    isSelected
                                                        ? 'bg-orange-600 text-white shadow-sm'
                                                        : 'border border-gray-200 bg-gray-50 text-gray-500 hover:border-orange-300 hover:text-orange-600'
                                                }`}
                                            >
                                                {sizeOption.label}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>

                            <div
                            dangerouslySetInnerHTML={{
                                __html: note?.noteText || '',
                            }}
                            className={`fix-nested-lists ${selectedNoteTextSizeClass} text-gray-700`} // 21 Jul 2026
                            // className="fix-nested-lists text-[11px] text-gray-700"
                            />

                            <div className="mt-2 flex justify-end">
                            <button
                                type="button"
                                onClick={() =>
                                handleOpenCoachNoteSession(note)
                                }
                                className="flex items-center rounded border border-gray-200 bg-white px-2 py-1 text-[10px] text-gray-600 hover:bg-gray-100"
                            >
                                Open Session Pad

                                <ArrowRightCircleIcon className="ml-1 h-4 w-4 text-lime-600" />
                            </button>
                            </div>
                        </div>
                        )}
                    </div>
                    )
                })}
                </div>
            </div>
            ) : (
            <div className="rounded bg-gray-50 p-3">
                <p className="text-xs text-gray-500">
                {selectedCoachNoteSessionId === 'all' &&
                selectedCoachNoteCategory === 'all'
                    ? 'No Coach notes are available in this trail yet.'
                    : 'No Coach notes match the selected Session and Category filters.'}
                </p>
            </div>
            )}
        </div>
        )}

       
        {/* 16 - Cohort engagement and coaching coverage section - 04 Jul 2026*/}
        {activeTrailBoardTab === 'cohort' && (
        <div className="space-y-2">
            {/* 16a - Selected Cohort header */}
            <div className="rounded bg-white p-3 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex min-w-0 items-center">
                <div className="mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-50">
                    <UserGroupIcon className="h-5 w-5 text-orange-600" />
                </div>

                <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-gray-800">
                    {selectedCohort?.cohortName || 'No Cohort Selected'}
                    </p>

                    <p className="text-[10px] text-gray-500">
                    Cohort coaching coverage across this trail.
                    </p>
                </div>
                </div>

                <div className="flex items-center">
                <p className="rounded-full bg-orange-50 px-2 py-1 text-[10px] font-semibold text-orange-700">
                    {selectedCohort?.cohortStatus || 'Status Not Set'}
                </p>
                </div>
            </div>

            {/* 16a1 - Selected Cohort planned dates */}
            {(selectedCohort?.plannedStartDate ||
                selectedCohort?.plannedEndDate) && (
                <div className="mt-2 border-t border-gray-100 pt-2">
                <p className="text-[10px] text-gray-500">
                    <span className="font-semibold text-gray-700">
                    Planned:
                    </span>{' '}
                    {formatTrailBoardDate(
                    selectedCohort?.plannedStartDate
                    )}{' '}
                    —{' '}
                    {formatTrailBoardDate(
                    selectedCohort?.plannedEndDate
                    )}
                </p>
                </div>
            )}
            </div>

            {/* 16b - Cohort coaching coverage summary */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
                {/* 16b1 - Cohort Users */}
                <div className="rounded bg-white p-3 shadow-sm">
                    <div className="flex items-center">
                    <UserGroupIcon className="mr-2 h-5 w-5 text-orange-600" />

                    <div>
                        <p className="text-[10px] text-gray-500">
                        Users
                        </p>

                        <p className="text-sm font-bold text-orange-600">
                        {cohortUserCount}
                        </p>
                    </div>
                    </div>
                </div>

                {/* 16b2 - Cohort Sessions */}
                <div className="rounded bg-white p-3 shadow-sm">
                    <div className="flex items-center">
                    <CalendarDaysIcon className="mr-2 h-5 w-5 text-orange-600" />

                    <div>
                        <p className="text-[10px] text-gray-500">
                        Sessions
                        </p>

                        <p className="text-sm font-bold text-orange-600">
                        {cohortSessionCount}
                        </p>
                    </div>
                    </div>
                </div>

                {/* 16b3 - Users with private Coach observations */}
                <div className="rounded bg-white p-3 shadow-sm">
                    <div className="flex items-center">
                    <DocumentTextIcon className="mr-2 h-5 w-5 text-orange-600" />

                    <div>
                        <p className="text-[10px] text-gray-500">
                        Self Notes
                        </p>

                        <p className="text-sm font-bold text-orange-600">
                        {cohortUsersWithSelfNotesCount}
                        </p>
                    </div>
                    </div>
                </div>

                {/* 16b4 - Users with individual shared Coach guidance */}
                <div className="rounded bg-white p-3 shadow-sm">
                    <div className="flex items-center">
                    <ChatBubbleLeftRightIcon className="mr-2 h-5 w-5 text-orange-600" />

                    <div>
                        <p className="text-[10px] text-gray-500">
                        Shared Notes
                        </p>

                        <p className="text-sm font-bold text-orange-600">
                        {cohortUsersWithSharedNotesCount}
                        </p>
                    </div>
                    </div>
                </div>

                {/* 16b5 - Users without individual Coach engagement */}
                <div className="rounded bg-white p-3 shadow-sm">
                    <div className="flex items-center">
                    <ArrowTrendingUpIcon className="mr-2 h-5 w-5 text-orange-600" />

                    <div>
                        <p className="text-[10px] text-gray-500">
                        Needs Attention
                        </p>

                        <p className="text-sm font-bold text-orange-600">
                        {cohortUsersNeedingAttentionCount}
                        </p>
                    </div>
                    </div>
                </div>
            </div>

            {/* 16c - Cohort member coaching coverage */}
            <div className="rounded bg-white p-3 shadow-sm">
            {/* 16c1 - Cohort member coverage title and filters */}
            <div className="mb-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                <div>
                    <p className="text-sm font-bold text-gray-800">
                    Cohort Members
                    </p>

                    <p className="text-[10px] text-gray-500">
                    Individual coaching coverage across the selected cohort.
                    </p>
                </div>

                <div className="flex h-5 min-w-5 items-center justify-center rounded-full border border-orange-300 bg-orange-50 px-1">
                    <p className="text-[10px] font-bold text-orange-600">
                    {filteredCohortMemberEngagements.length}
                    </p>
                </div>
                </div>

                {/* 16c2 - Cohort member coverage status filters */}
                <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={() =>
                    setSelectedCohortCoverageFilter('all')
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                    selectedCohortCoverageFilter === 'all'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                >
                    All ({cohortMemberEngagements.length})
                </button>

                <button
                    type="button"
                    onClick={() =>
                    setSelectedCohortCoverageFilter(
                        'Needs Attention'
                    )
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                    selectedCohortCoverageFilter ===
                    'Needs Attention'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                >
                    Needs Attention ({cohortUsersNeedingAttentionCount})
                </button>

                <button
                    type="button"
                    onClick={() =>
                    setSelectedCohortCoverageFilter('Engaged')
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                    selectedCohortCoverageFilter === 'Engaged'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                >
                    Engaged ({cohortUsersWithSelfNotesCount})
                </button>

                <button
                    type="button"
                    onClick={() =>
                    setSelectedCohortCoverageFilter(
                        'Shared Guidance'
                    )
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                    selectedCohortCoverageFilter ===
                    'Shared Guidance'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                >
                    Shared Guidance ({cohortMemberEngagements.filter(
                    (member) =>
                        member?.engagementStatus ===
                        'Shared Guidance'
                    ).length})
                </button>
                </div>
            </div>

                        

                        {filteredCohortMemberEngagements.length > 0 ? (
                            <div className="overflow-x-auto">
                            <div className="min-w-[1000px]">
                                {/* 16c2 - Cohort member table header */}
                                <div className="grid grid-cols-12 rounded bg-gray-100 px-2 py-2">
                                <div className="col-span-3">
                                    <p className="text-[10px] font-bold text-gray-600">
                                    User
                                    </p>
                                </div>

                                <div className="col-span-2">
                                    <p className="text-[10px] font-bold text-gray-600">
                                    Sessions
                                    </p>
                                </div>

                                <div className="col-span-2">
                                    <p className="text-[10px] font-bold text-gray-600">
                                    Self Notes
                                    </p>
                                </div>

                                <div className="col-span-2">
                                    <p className="text-[10px] font-bold text-gray-600">
                                    Shared Notes
                                    </p>
                                </div>

                                <div className="col-span-2">
                                    <p className="text-[10px] font-bold text-gray-600">
                                    Last Activity
                                    </p>
                                </div>

                                <div className="col-span-1">
                                    <p className="text-[10px] font-bold text-gray-600">
                                    Status
                                    </p>
                                </div>
                                </div>

                                {/* 16c3 - Cohort member rows */}
                                {filteredCohortMemberEngagements.map((member, memberIndex) => {
                                const isExpanded =
                                    showCohortMemberDetails[
                                    member?.userId
                                    ]

                                return (
                                    <div
                                    key={member?.userId || memberIndex}
                                    className="border-b border-gray-100 px-2 py-2 last:border-b-0 hover:bg-orange-50"
                                    >
                                    <div className="grid grid-cols-12 items-center">
                                        {/* 16c3a - User identity and expand control */}
                                        <div className="col-span-3 flex min-w-0 items-center pr-2">
                                        <p className="mr-1 text-sm font-bold text-gray-800">
                                            {memberIndex + 1}.
                                        </p>

                                        <button
                                            type="button"
                                            onClick={() =>
                                            handleToggleCohortMemberDetails(
                                                member?.userId
                                            )
                                            }
                                            className="mr-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-white"
                                            title={
                                            isExpanded
                                                ? 'Hide session engagement detail'
                                                : 'Show session engagement detail'
                                            }
                                        >
                                            {isExpanded ? (
                                            <MinusIcon className="h-4 w-4 text-orange-600" />
                                            ) : (
                                            <PlusIcon className="h-4 w-4 text-orange-600" />
                                            )}
                                        </button>

                                        <div className="min-w-0">
                                            <p className="truncate text-[11px] font-bold text-gray-700">
                                            {member?.userName}
                                            </p>

                                            {member?.userEmail && (
                                            <p className="truncate text-[9px] text-gray-400">
                                                {member.userEmail}
                                            </p>
                                            )}
                                        </div>
                                        </div>

                                        {/* 16c3b - Planned session coverage */}
                                        <div className="col-span-2">
                                        <p className="text-[11px] font-semibold text-gray-700">
                                            {member?.plannedSessionCount || 0}
                                        </p>
                                        </div>

                                        {/* 16c3c - Private Coach note count */}
                                        <div className="col-span-2">
                                        <p className="text-[11px] font-semibold text-gray-700">
                                            {member?.selfForUserNoteCount || 0}
                                        </p>
                                        </div>

                                        {/* 16c3d - Individual shared Coach note count */}
                                        <div className="col-span-2">
                                        <p className="text-[11px] font-semibold text-gray-700">
                                            {member?.sharedWithUserNoteCount || 0}
                                        </p>
                                        </div>

                                        {/* 16c3e - Most recent individual Coach activity */}
                                        <div className="col-span-2">
                                        {member?.latestCoachActivityDate ? (
                                            <>
                                            <p className="w-fit rounded-full bg-lime-200 px-2 py-[1px] text-[9px] text-gray-700">
                                                {getTimeSince(
                                                member.latestCoachActivityDate
                                                )}
                                            </p>

                                            <p className="mt-0.5 text-[7px] italic text-gray-400">
                                                last updated
                                            </p>
                                            </>
                                        ) : (
                                            <p className="text-[10px] text-gray-400">
                                            —
                                            </p>
                                        )}
                                        </div>

                                        {/* 16c3f - Coach coverage status */}
                                        <div className="col-span-1">
                                        <p
                                            className={`w-fit rounded-full px-2 py-1 text-[9px] font-semibold ${
                                            member?.engagementStatus === 'Engaged'
                                                ? 'bg-orange-100 text-orange-700'
                                                : member?.engagementStatus ===
                                                'Shared Guidance'
                                                ? 'bg-lime-100 text-lime-700'
                                                : 'bg-gray-200 text-gray-600'
                                            }`}
                                        >
                                            {member?.engagementStatus}
                                        </p>
                                        </div>
                                    </div>

                                    {/* 16c4 - Expanded per-session member engagement */}
                                    {isExpanded && (
                                        <div className="m-1 mt-2 rounded bg-gray-50 p-2">
                                        <div className="mb-2">
                                            <p className="text-[10px] font-bold text-gray-700">
                                            Session Engagement
                                            </p>

                                            <p className="text-[9px] text-gray-500">
                                            Individual coaching notes for {member?.userName}.
                                            </p>
                                        </div>

                                        {member?.sessionBreakdown?.length > 0 ? (
                                            <div className="space-y-1">
                                            {member.sessionBreakdown.map(
                                                (session, sessionIndex) => {
                                                const hasSessionEngagement =
                                                    session?.selfForUserNoteCount > 0 ||
                                                    session?.sharedWithUserNoteCount > 0

                                                return (
                                                    <div
                                                    key={
                                                        session?.sessionId ||
                                                        sessionIndex
                                                    }
                                                    className="grid grid-cols-12 items-center rounded bg-white p-2"
                                                    >
                                                    {/* 16c4a - Session identity */}
                                                    <div className="col-span-4 flex min-w-0 items-center">
                                                        <div className="mr-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-orange-300 bg-orange-50">
                                                        <p className="text-[10px] font-bold text-orange-600">
                                                            {session?.sessionSequence ||
                                                            sessionIndex + 1}
                                                        </p>
                                                        </div>

                                                        <p className="truncate text-[10px] font-semibold text-gray-700">
                                                        {session?.sessionName}
                                                        </p>
                                                    </div>

                                                    {/* 16c4b - Private note count */}
                                                    <div className="col-span-2">
                                                        <p className="text-[10px] text-gray-600">
                                                        Self:{' '}
                                                        <span className="font-bold">
                                                            {session?.selfForUserNoteCount || 0}
                                                        </span>
                                                        </p>
                                                    </div>

                                                    {/* 16c4c - Shared note count */}
                                                    <div className="col-span-2">
                                                        <p className="text-[10px] text-gray-600">
                                                        Shared:{' '}
                                                        <span className="font-bold">
                                                            {session?.sharedWithUserNoteCount ||
                                                            0}
                                                        </span>
                                                        </p>
                                                    </div>

                                                    {/* 16c4d - Session engagement state */}
                                                    <div className="col-span-2">
                                                        <p
                                                        className={`w-fit rounded-full px-2 py-1 text-[9px] font-semibold ${
                                                            hasSessionEngagement
                                                            ? 'bg-lime-100 text-lime-700'
                                                            : 'bg-gray-100 text-gray-500'
                                                        }`}
                                                        >
                                                        {hasSessionEngagement
                                                            ? 'Engaged'
                                                            : 'No Notes'}
                                                        </p>
                                                    </div>

                                                    {/* 16c4e - Open source Session Pad */}
                                                    <div className="col-span-2 flex justify-end">
                                                        <button
                                                        type="button"
                                                        onClick={() =>
                                                            navigate(
                                                            `/etapp/coach/update/${coachId}/${orgId}/${trailId}/${session?.sessionId}`
                                                            )
                                                        }
                                                        className="flex items-center rounded border border-gray-200 bg-white px-2 py-1 text-[9px] text-gray-600 hover:bg-gray-100"
                                                        >
                                                        Open

                                                        <ArrowRightCircleIcon className="ml-1 h-4 w-4 text-lime-600" />
                                                        </button>
                                                    </div>
                                                    </div>
                                                )
                                                }
                                            )}
                                            </div>
                                        ) : (
                                            <p className="text-[10px] text-gray-400">
                                            No active session plans are available for this user
                                            in the selected cohort.
                                            </p>
                                        )}
                                        </div>
                                    )}
                                    </div>
                                )
                                })}
                            </div>
                            </div>
                        ) : (
                            <div className="rounded bg-gray-50 p-3">
                            <p className="text-xs text-gray-500"> {selectedCohortCoverageFilter === 'all' ? 'No active users are available in the selected cohort.' : 'No cohort members match the selected coaching coverage filter.'} </p>
                            </div>
                        )}
            </div>
         </div>
        )}


        {/* 17 - Cohort scoped Session Q&A section */}
        {activeTrailBoardTab === 'sessionQuestions' && (
        <div className="rounded bg-white p-3 shadow-sm">
            {/* 17a - Session Q&A title and selected cohort context */}
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
            <div>
                <p className="text-sm font-bold text-gray-800">
                Session Q&amp;As
                </p>

                <p className="text-[10px] text-gray-500">
                Cohort responses and Coach engagement across trail sessions.
                </p>
            </div>

            <div className="flex items-center rounded-full border border-orange-300 bg-orange-50 px-2 py-1">
                <UserGroupIcon className="mr-1 h-4 w-4 text-orange-600" />

                <p className="text-[10px] font-semibold text-orange-700">
                {selectedCohort?.cohortName || 'No Cohort Selected'}
                </p>
            </div>
            </div>

            {/* 17b - Q&A enabled Session chips */}
            {trailQNASessions.length > 0 ? (
            <>
                <div className="mb-3 flex flex-wrap gap-2 border-b border-gray-100 pb-3">
                {trailQNASessions.map((session) => {
                    const isSelected =
                    session?.sessionId?.toString() ===
                    selectedTrailQNASessionId?.toString()

                    const activeQNASetCount =
                    session?.sessionQNASets?.length || 0

                    return (
                    <button
                        key={session.sessionId}
                        type="button"
                        onClick={() =>
                        setSelectedTrailQNASessionId(
                            session.sessionId
                        )
                        }
                        className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                        isSelected
                            ? 'bg-orange-600 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                    >
                        {session?.sessionSequence
                        ? `Session ${session.sessionSequence}`
                        : session?.sessionName}{' '}
                        ({activeQNASetCount})
                    </button>
                    )
                })}
                </div>

                {/* 17c - Selected Session Q&A Board scoped to selected cohort */}
                {selectedTrailQNASession ? (
                <div className="rounded bg-gray-50 p-1">
                    <div className="m-1 flex flex-wrap items-center justify-between gap-2 rounded bg-white p-2">
                    <div className="flex min-w-0 items-center">
                        <div className="mr-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-orange-300 bg-orange-50">
                        <p className="text-[10px] font-bold text-orange-600">
                            {selectedTrailQNASession?.sessionSequence || '?'}
                        </p>
                        </div>

                        <div className="min-w-0">
                        <p className="truncate text-xs font-bold text-gray-700">
                            {selectedTrailQNASession?.sessionName}
                        </p>

                        <p className="text-[10px] text-gray-500">
                            {selectedTrailQNASession?.sessionQNASets?.length || 0}{' '}
                            active Q&amp;A set
                            {(selectedTrailQNASession?.sessionQNASets?.length || 0) !==
                            1
                            ? 's'
                            : ''}
                        </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() =>
                        navigate(
                            `/etapp/coach/update/${coachId}/${orgId}/${trailId}/${selectedTrailQNASession?.sessionId}`
                        )
                        }
                        className="flex items-center rounded border border-gray-200 bg-white px-2 py-1 text-[10px] text-gray-600 hover:bg-gray-100"
                    >
                        Open Session Pad

                        <ArrowRightCircleIcon className="ml-1 h-4 w-4 text-lime-600" />
                    </button>
                    </div>

                    <EtUserSessionQNABoard
                    userId={coachId}
                    userOrgId={coachOrgId}
                    trailId={trailId}
                    sessionId={selectedTrailQNASession.sessionId}
                    sessionQNASets={
                        selectedTrailQNASession.sessionQNASets
                    }
                    responseRole="Coach"
                    boardTitle="Coach Session Q&A Board"
                    cohortId={selectedCohortId}
                    cohort={selectedCohort}
                    />
                </div>
                ) : (
                <div className="rounded bg-gray-50 p-3">
                    <p className="text-xs text-gray-500">
                    Select a Session to view its cohort Session Q&amp;A Board.
                    </p>
                </div>
                )}
            </>
            ) : (
            <div className="rounded bg-gray-50 p-3">
                <p className="text-xs text-gray-500">
                No active Session Q&amp;A sets are available in this trail.
                </p>
            </div>
            )}
        </div>
        )}

        {/* 18 - Cohort scoped Coach Evaluations */}
        {activeTrailBoardTab === 'evaluations' && (
        <div className="space-y-2">
            {/* 18a - Evaluations title and selected cohort context */}
            <div className="rounded bg-white p-3 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex min-w-0 items-center">
                <div className="mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-50">
                    <ArrowTrendingUpIcon className="h-5 w-5 text-orange-600" />
                </div>

                <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-gray-800">
                    Evaluations
                    </p>

                    <p className="text-[10px] text-gray-500">
                    Coach challenge evaluation coverage across the selected cohort.
                    </p>
                </div>
                </div>

                <div className="flex items-center rounded-full border border-orange-300 bg-orange-50 px-2 py-1">
                <UserGroupIcon className="mr-1 h-4 w-4 text-orange-600" />

                <p className="text-[10px] font-semibold text-orange-700">
                    {selectedCohort?.cohortName || 'No Cohort Selected'}
                </p>
                </div>
            </div>
            </div>

            {/* 18b - Evaluation summary cards */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-6">
            {/* 18b1 - Evaluated users */}
            <div className="rounded bg-white p-3 shadow-sm">
                <div className="flex items-center">
                <UserGroupIcon className="mr-2 h-5 w-5 text-orange-600" />

                <div>
                    <p className="text-[10px] text-gray-500">
                    Evaluated Users
                    </p>

                    <p className="text-sm font-bold text-orange-600">
                    {cohortEvaluatedUserCount}
                    </p>
                </div>
                </div>
            </div>

            {/* 18b2 - Evaluation started users */}
            <div className="rounded bg-white p-3 shadow-sm">
                <div className="flex items-center">
                <DocumentTextIcon className="mr-2 h-5 w-5 text-orange-600" />

                <div>
                    <p className="text-[10px] text-gray-500">
                    Started
                    </p>

                    <p className="text-sm font-bold text-orange-600">
                    {cohortEvaluationStartedUserCount}
                    </p>
                </div>
                </div>
            </div>

            {/* 18b3 - Users needing evaluation */}
            <div className="rounded bg-white p-3 shadow-sm">
                <div className="flex items-center">
                <ArrowTrendingUpIcon className="mr-2 h-5 w-5 text-orange-600" />

                <div>
                    <p className="text-[10px] text-gray-500">
                    Needs Evaluation
                    </p>

                    <p className="text-sm font-bold text-orange-600">
                    {cohortUsersNeedingEvaluationCount}
                    </p>
                </div>
                </div>
            </div>

            {/* 18b4 - Evaluation records */}
            <div className="rounded bg-white p-3 shadow-sm">
                <div className="flex items-center">
                <DocumentTextIcon className="mr-2 h-5 w-5 text-orange-600" />

                <div>
                    <p className="text-[10px] text-gray-500">
                    Evaluation Records
                    </p>

                    <p className="text-sm font-bold text-orange-600">
                    {cohortEvaluationRecordCount}
                    </p>
                </div>
                </div>
            </div>

            {/* 18b5 - Dimensions scored */}
            <div className="rounded bg-white p-3 shadow-sm">
                <div className="flex items-center">
                <ChatBubbleLeftRightIcon className="mr-2 h-5 w-5 text-orange-600" />

                <div>
                    <p className="text-[10px] text-gray-500">
                    Dimensions Scored
                    </p>

                    <p className="text-sm font-bold text-orange-600">
                    {cohortScoredDimensionCount}
                    </p>
                </div>
                </div>
            </div>

            {/* 18b6 - Cohort average Coach score */}
            <div className="rounded bg-white p-3 shadow-sm">
                <div className="flex items-center">
                <ArrowTrendingUpIcon className="mr-2 h-5 w-5 text-orange-600" />

                <div>
                    <p className="text-[10px] text-gray-500">
                    Average Score
                    </p>

                    <p className="text-sm font-bold text-orange-600">
                    {cohortAverageEvaluationScore !== null
                        ? cohortAverageEvaluationScore.toFixed(1)
                        : '—'}
                    </p>
                </div>
                </div>
            </div>
            </div>

            {/* 18c - Evaluation coverage table */}
            <div className="rounded bg-white p-3 shadow-sm">
            {/* 18c1 - Evaluation table header and filters */}
            <div className="mb-3 space-y-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <p className="text-sm font-bold text-gray-800">
                    Cohort Evaluation Coverage
                    </p>

                    <p className="text-[10px] text-gray-500">
                    Coach challenge evaluation status for each user in the selected cohort.
                    </p>
                </div>

                <div className="flex h-5 min-w-5 items-center justify-center rounded-full border border-orange-300 bg-orange-50 px-1">
                    <p className="text-[10px] font-bold text-orange-600">
                    {filteredCohortEvaluationCoverage.length}
                    </p>
                </div>
                </div>

                {/* 18c2 - Session and user filter chips */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-2">
                {/* 18c2a - Session filters */}
                <div className="flex flex-wrap gap-2">
                    <button
                    type="button"
                    onClick={() =>
                        setSelectedEvaluationSessionId('all')
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                        selectedEvaluationSessionId === 'all'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    >
                    All Sessions ({selectedCohortCoachEvals.length})
                    </button>

                    {evaluationSessionFilters.map((session) => {
                    const isSelected =
                        session?.sessionId?.toString() ===
                        selectedEvaluationSessionId?.toString()

                    return (
                        <button
                        key={session?.sessionId}
                        type="button"
                        onClick={() =>
                            setSelectedEvaluationSessionId(
                            session?.sessionId
                            )
                        }
                        className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                            isSelected
                            ? 'bg-orange-600 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                        >
                        {session?.sessionSequence
                            ? `Session ${session.sessionSequence}`
                            : session?.sessionName}{' '}
                        ({session?.sessionEvaluationCount || 0})
                        </button>
                    )
                    })}
                </div>

                {/* 18c2b - User filters */}
                <div className="flex flex-wrap justify-end gap-2">
                    <button
                    type="button"
                    onClick={() =>
                        setSelectedEvaluationUserId('all')
                    }
                    className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                        selectedEvaluationUserId === 'all'
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    >
                    All Users
                    </button>

                    {selectedCohortUsers.map((user) => {
                    const isSelected =
                        user?._id?.toString() ===
                        selectedEvaluationUserId?.toString()

                    return (
                        <button
                        key={user?._id}
                        type="button"
                        onClick={() =>
                            setSelectedEvaluationUserId(user?._id)
                        }
                        className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
                            isSelected
                            ? 'bg-orange-600 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                        title={user?.name || ''}
                        >
                        {getFirstName(user?.name)}
                        </button>
                    )
                    })}
                </div>
                </div>
            </div>

            {/* 18d - Evaluation coverage rows */}
            {filteredCohortEvaluationCoverage.length > 0 ? (
                <div className="overflow-x-auto">
                <div className="min-w-[1100px]">
                    {/* 18d1 - Table header */}
                    <div className="grid grid-cols-12 rounded bg-gray-100 px-2 py-2">
                    <div className="col-span-3">
                        <p className="text-[10px] font-bold text-gray-600">
                        User
                        </p>
                    </div>

                    <div className="col-span-2">
                        <p className="text-[10px] font-bold text-gray-600">
                        Sessions
                        </p>
                    </div>

                    <div className="col-span-2">
                        <p className="text-[10px] font-bold text-gray-600">
                        Challenges
                        </p>
                    </div>

                    <div className="col-span-1">
                        <p className="text-[10px] font-bold text-gray-600">
                        Dimensions
                        </p>
                    </div>

                    <div className="col-span-1">
                        <p className="text-[10px] font-bold text-gray-600">
                        Avg Score
                        </p>
                    </div>

                    <div className="col-span-2">
                        <p className="text-[10px] font-bold text-gray-600">
                        Last Evaluation
                        </p>
                    </div>

                    <div className="col-span-1">
                        <p className="text-[10px] font-bold text-gray-600">
                        Status
                        </p>
                    </div>
                    </div>

                    {/* 18d2 - User coverage rows */}
                    {filteredCohortEvaluationCoverage.map(
                    (userCoverage, userIndex) => {
                        const isExpanded =
                        showEvaluationUserDetails[
                            userCoverage?.userId
                        ]

                        return (
                        <div
                            key={userCoverage?.userId || userIndex}
                            className="border-b border-gray-100 px-2 py-2 last:border-b-0 hover:bg-orange-50"
                        >
                            <div className="grid grid-cols-12 items-center">
                            {/* 18d2a - User identity */}
                            <div className="col-span-3 flex min-w-0 items-center pr-2">
                                <p className="mr-1 text-sm font-bold text-gray-800">
                                {userIndex + 1}.
                                </p>

                                <button
                                type="button"
                                onClick={() =>
                                    handleToggleEvaluationUserDetails(
                                    userCoverage?.userId
                                    )
                                }
                                className="mr-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-white"
                                title={
                                    isExpanded
                                    ? 'Hide evaluation detail'
                                    : 'Show evaluation detail'
                                }
                                >
                                {isExpanded ? (
                                    <MinusIcon className="h-4 w-4 text-orange-600" />
                                ) : (
                                    <PlusIcon className="h-4 w-4 text-orange-600" />
                                )}
                                </button>

                                <div className="min-w-0">
                                <p className="truncate text-[11px] font-bold text-gray-700">
                                    {userCoverage?.userName}
                                </p>

                                {userCoverage?.userEmail && (
                                    <p className="truncate text-[9px] text-gray-400">
                                    {userCoverage.userEmail}
                                    </p>
                                )}
                                </div>
                            </div>

                            {/* 18d2b - Sessions evaluated */}
                            <div className="col-span-2">
                                <p className="text-[11px] font-semibold text-gray-700">
                                {userCoverage?.evaluatedSessionCount || 0}
                                </p>
                            </div>

                            {/* 18d2c - Challenges evaluated */}
                            <div className="col-span-2">
                                <p className="text-[11px] font-semibold text-gray-700">
                                {userCoverage?.evaluatedChallengeCount || 0}
                                </p>
                            </div>

                            {/* 18d2d - Scored dimensions */}
                            <div className="col-span-1">
                                <p className="text-[11px] font-semibold text-gray-700">
                                {userCoverage?.scoredDimensionCount || 0}
                                </p>
                            </div>

                            {/* 18d2e - User average score */}
                            <div className="col-span-1">
                                <p className="text-[11px] font-semibold text-gray-700">
                                {userCoverage?.averageScore !== null &&
                                userCoverage?.averageScore !== undefined
                                    ? userCoverage.averageScore.toFixed(1)
                                    : '—'}
                                </p>
                            </div>

                            {/* 18d2f - Latest evaluation */}
                            <div className="col-span-2">
                                {userCoverage?.latestEvaluationDate ? (
                                <>
                                    <p className="w-fit rounded-full bg-lime-200 px-2 py-[1px] text-[9px] text-gray-700">
                                    {getTimeSince(
                                        userCoverage.latestEvaluationDate
                                    )}
                                    </p>

                                    <p className="mt-0.5 text-[7px] italic text-gray-400">
                                    last updated
                                    </p>
                                </>
                                ) : (
                                <p className="text-[10px] text-gray-400">
                                    —
                                </p>
                                )}
                            </div>

                            {/* 18d2g - Evaluation status */}
                            <div className="col-span-1">
                                <p
                                className={`w-fit rounded-full px-2 py-1 text-[9px] font-semibold ${
                                    userCoverage?.evaluationStatus ===
                                    'Evaluated'
                                    ? 'bg-orange-100 text-orange-700'
                                    : userCoverage?.evaluationStatus ===
                                        'Evaluation Started'
                                    ? 'bg-lime-100 text-lime-700'
                                    : 'bg-gray-200 text-gray-600'
                                }`}
                                >
                                {userCoverage?.evaluationStatus}
                                </p>
                            </div>
                            </div>

                            {/* 18e - Expanded user evaluation detail */}
                            {isExpanded && (
                            <div className="m-1 mt-2 rounded bg-gray-50 p-2">
                                <div className="mb-2">
                                <p className="text-[10px] font-bold text-gray-700">
                                    Evaluation Detail
                                </p>

                                <p className="text-[9px] text-gray-500">
                                    Coach evaluations recorded for{' '}
                                    {userCoverage?.userName}.
                                </p>
                                </div>

                                {userCoverage?.evaluationDetails?.length > 0 ? (
                                <div className="space-y-2">
                                    {userCoverage.evaluationDetails.map(
                                    (
                                        evaluationDetail,
                                        evaluationIndex
                                    ) => (
                                        <div
                                        key={
                                            evaluationDetail?.evaluationId ||
                                            `${evaluationDetail?.sessionId}-${evaluationDetail?.challengeId}-${evaluationIndex}`
                                        }
                                        className="rounded bg-white p-2"
                                        >
                                        {/* 18e1 - Session and challenge heading */}
                                        <div className="flex flex-wrap items-start justify-between gap-2 border-b border-gray-100 pb-2">
                                            <div className="flex min-w-0 items-center">
                                            <div className="mr-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-orange-300 bg-orange-50">
                                                <p className="text-[10px] font-bold text-orange-600">
                                                {evaluationDetail?.sessionSequence ||
                                                    '?'}
                                                </p>
                                            </div>

                                            <div className="min-w-0">
                                                <p className="truncate text-[10px] font-bold text-gray-700">
                                                {evaluationDetail?.sessionName}
                                                </p>

                                                <p className="truncate text-[10px] text-orange-600">
                                                {evaluationDetail?.challengeName}
                                                </p>
                                            </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                            <p className="rounded-full bg-orange-50 px-2 py-1 text-[9px] font-semibold text-orange-700">
                                                {evaluationDetail?.scoredDimensionCount || 0}{' '}
                                                Scored
                                            </p>

                                            <p className="rounded-full bg-lime-100 px-2 py-1 text-[9px] font-semibold text-lime-700">
                                                Avg:{' '}
                                                {evaluationDetail?.averageScore !== null &&
                                                evaluationDetail?.averageScore !== undefined
                                                ? evaluationDetail.averageScore.toFixed(
                                                    1
                                                    )
                                                : '—'}
                                            </p>
                                            </div>
                                        </div>

                                        {/* 18e2 - Dimension evaluation detail */}
                                        <div className="mt-2 space-y-1">
                                            {evaluationDetail?.dimensionEvaluations?.map(
                                            (
                                                dimensionEvaluation,
                                                dimensionIndex
                                            ) => {
                                                const score =
                                                getValidCoachScore(
                                                    dimensionEvaluation?.userChallengeDimCoachScore
                                                )

                                                return (
                                                <div
                                                    key={
                                                    dimensionEvaluation?._id ||
                                                    dimensionEvaluation?.userChallengeDimId ||
                                                    dimensionIndex
                                                    }
                                                    className="grid grid-cols-12 items-start rounded bg-gray-50 p-2"
                                                >
                                                    <div className="col-span-2">
                                                    <p className="text-[9px] font-bold text-gray-500">
                                                        Dimension
                                                    </p>

                                                    <p className="mt-1 text-[10px] font-bold text-gray-700">
                                                        {dimensionIndex + 1}
                                                    </p>
                                                    </div>

                                                    <div className="col-span-2">
                                                    <p className="text-[9px] font-bold text-gray-500">
                                                        Score
                                                    </p>

                                                    <p className="mt-1 text-[11px] font-bold text-orange-600">
                                                        {score !== null
                                                        ? score
                                                        : '—'}
                                                    </p>
                                                    </div>

                                                    <div className="col-span-8">
                                                    <p className="text-[9px] font-bold text-gray-500">
                                                        Coach Evaluation
                                                    </p>

                                                    <p className="mt-1 text-[10px] text-gray-700">
                                                        {dimensionEvaluation?.userChallengeDimCoachEvalText ||
                                                        'No supporting Coach evaluation text added.'}
                                                    </p>
                                                    </div>
                                                </div>
                                                )
                                            }
                                            )}
                                        </div>

                                        {/* 18e3 - Open Session Pad */}
                                        <div className="mt-2 flex justify-end">
                                            <button
                                            type="button"
                                            onClick={() =>
                                                navigate(
                                                `/etapp/coach/update/${coachId}/${orgId}/${trailId}/${evaluationDetail?.sessionId}`
                                                )
                                            }
                                            className="flex items-center rounded border border-gray-200 bg-white px-2 py-1 text-[9px] text-gray-600 hover:bg-gray-100"
                                            >
                                            Open Session Pad

                                            <ArrowRightCircleIcon className="ml-1 h-4 w-4 text-lime-600" />
                                            </button>
                                        </div>
                                        </div>
                                    )
                                    )}
                                </div>
                                ) : (
                                <p className="text-[10px] text-gray-400">
                                    No Coach challenge evaluations are available for this user.
                                </p>
                                )}
                            </div>
                            )}
                        </div>
                        )
                    }
                    )}
                </div>
                </div>
            ) : (
                <div className="rounded bg-gray-50 p-3">
                <p className="text-xs text-gray-500">
                    {selectedEvaluationSessionId === 'all' &&
                    selectedEvaluationUserId === 'all'
                    ? 'No active users are available in the selected cohort.'
                    : 'No cohort members match the selected evaluation filters.'}
                </p>
                </div>
            )}
            </div>
        </div>
        )}
      </div>
    )
}

export default EtCoachOrgTrailBoardCompProd